import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import salairesApi from '../services/salaires';
import plApi from '../services/pl';
import MasseSalariale from './MasseSalariale';
import { parseBulletinPdf } from '../utils/parseBulletinPdf';
import './Salaires.css';

const MOIS = ['Jan','Fev','Mar','Avr','Mai','Jun','Jul','Aou','Sep','Oct','Nov','Dec'];

// Parse 'YYYY-MM-DD' en date locale (pg renvoie les DATE en chaine brute)
function dateLocale(str) {
  if (!str) return null;
  const [y, m, d] = String(str).substring(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function dateFr(str) {
  const d = dateLocale(str);
  return d ? d.toLocaleDateString('fr-FR') : null;
}

// « 12 a 3 m » — compact pour tenir dans une carte
function ancienneteTexte(str, compact = true) {
  const d = dateLocale(str);
  if (!d) return null;
  const now = new Date();
  let mois = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (now.getDate() < d.getDate()) mois -= 1;
  if (mois < 0) return null;
  const a = Math.floor(mois / 12), m = mois % 12;
  if (compact) return a > 0 ? `${a} a${m ? ` ${m} m` : ''}` : `${m} m`;
  return a > 0 ? `${a} an${a > 1 ? 's' : ''}${m ? ` et ${m} mois` : ''}` : `${m} mois`;
}

// Champs optionnels affichables sur les cartes de l'organigramme
const ORG_CHAMPS = [
  { cle: 'contrat', label: 'Type de contrat', court: 'Contrat', valeur: (s) => s.type_contrat || null },
  { cle: 'fin', label: 'Date de fin prevue', court: 'Fin', valeur: (s) => dateFr(s.date_fin_contrat) },
  { cle: 'anciennete', label: 'Anciennete', court: 'Anc.', valeur: (s) => ancienneteTexte(s.date_anciennete) },
  { cle: 'base', label: 'Dernier salaire de base', court: 'Base', sensible: true, valeur: (s, m) => (m && m.salaire_base != null ? `${fmt(m.salaire_base)} €` : null) },
  { cle: 'brut', label: 'Moyenne brut (6 mois)', court: 'Brut', sensible: true, valeur: (s, m) => (m && m.moyenne_brut != null ? `${fmt(m.moyenne_brut)} €` : null) },
  { cle: 'cout', label: 'Cout employeur', court: 'Cout', sensible: true, valeur: (s, m) => (m && m.cout_employeur != null ? `${fmt(m.cout_employeur)} €` : null) },
  { cle: 'branche_masse', label: 'Masse salariale de la branche', court: 'Branche', sensible: true, valeur: (s, m, a) => (a && a.masse ? `${fmt(a.masse)} €` : null) },
  { cle: 'branche_effectif', label: 'Effectif de la branche', court: 'Effectif', valeur: (s, m, a) => (a ? `${a.effectif}${a.directs ? ` (${a.directs} dir.)` : ''}` : null) },
  { cle: 'absences', label: 'Absences (reel)', court: 'Abs.', sensible: true, valeur: (s, m) => {
    if (!m) return null;
    const inj = m.mois_injustifiees || 0, mal = m.mois_maladie || 0;
    if (!inj && !mal) return null;
    return [inj ? `${inj} inj` : null, mal ? `${mal} mal` : null].filter(Boolean).join(' · ');
  } },
];

// Contrat arrivant a echeance dans les 90 jours
function echeanceProche(str) {
  const d = dateLocale(str);
  if (!d) return false;
  const j = Math.round((d - new Date()) / 86400000);
  return j >= 0 && j <= 90;
}

const TYPES_CONTRAT = ['CDI', 'CDD', 'Alternance', 'Interim', 'Stage', 'Apprentissage'];

function fmt(n) {
  if (n === null || n === undefined) return '-';
  return Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// mode = 'salaires' (page Salaries) | 'organigramme' (page dediee du menu lateral)
export default function Salaires({ user, isAdmin, mode = 'salaires' }) {
  const isOrgMode = mode === 'organigramme';
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Filters
  const [exercices, setExercices] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [activites, setActivites] = useState([]);
  const [selectedExercice, setSelectedExercice] = useState('');
  const [selectedSociete, setSelectedSociete] = useState('');
  const [selectedActivite, setSelectedActivite] = useState('global');
  const [search, setSearch] = useState('');

  // Data
  const [salaries, setSalaries] = useState([]);
  const [allSalaries, setAllSalaries] = useState([]);
  const [details, setDetails] = useState([]);
  const [typesCharges, setTypesCharges] = useState([]);
  const [postes, setPostes] = useState([]);
  const [services, setServices] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [totalVerse, setTotalVerse] = useState([]);
  const [moisType, setMoisType] = useState([]);
  const [coeffDefault, setCoeffDefault] = useState(1.45);
  const [showCoeffModal, setShowCoeffModal] = useState(false);
  const [coeffInput, setCoeffInput] = useState('');

  // View
  const [activeView, setActiveView] = useState(isOrgMode ? 'organigramme' : 'liste');
  const [selectedSalarie, setSelectedSalarie] = useState(null);
  const [filterEcarts, setFilterEcarts] = useState(false);
  const [filterSansActivite, setFilterSansActivite] = useState(false);
  const [expandedSalaries, setExpandedSalaries] = useState({});

  // Edit
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [newSalarie, setNewSalarie] = useState({ nom: '', prenom: '', societe_id: '', activite_id: '', categorie: 'productif' });

  // Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [purgeConfirm, setPurgeConfirm] = useState(null); // { mois, label }
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Add charge line per salarie
  const [addingChargeFor, setAddingChargeFor] = useState(null); // salarie_id
  const [newChargeTypeId, setNewChargeTypeId] = useState('');

  // Edit nom/prenom
  const [editingName, setEditingName] = useState(null); // salarie_id
  const [editNameVal, setEditNameVal] = useState({ nom: '', prenom: '' });

  // Edit annuaire (inline)
  const [editingAnnuaire, setEditingAnnuaire] = useState(null); // salarie_id
  const [editAnnuaireVal, setEditAnnuaireVal] = useState({});

  // Import PDF annuaire
  const [annuairePdfPreview, setAnnuairePdfPreview] = useState(null);
  const [annuairePdfImporting, setAnnuairePdfImporting] = useState(false);

  // Tri annuaire
  const [sortAnnuaire, setSortAnnuaire] = useState({ col: 'nom', dir: 'asc' });
  // Tri de « Detail par salarie » : 'nom', 'total', ou un numero de mois (1-12)
  const [sortDetail, setSortDetail] = useState({ col: 'nom', dir: 'asc' });

  // Licenciements
  const [licenciements, setLicenciements] = useState([]);
  const [licAddSalarieId, setLicAddSalarieId] = useState('');
  const [licAddMotif, setLicAddMotif] = useState('');
  const [licAddDate, setLicAddDate] = useState('');
  const [licAddCommentaire, setLicAddCommentaire] = useState('');
  const [licEditingId, setLicEditingId] = useState(null);
  const [licEditVal, setLicEditVal] = useState({});

  // Projeter (prévisions) — par salarié
  const [projeterFor, setProjecterFor] = useState(null); // salarie_id
  const [projeterMoisSource, setProjecterMoisSource] = useState('');

  // Organigramme layout
  const [orgDragId, setOrgDragId] = useState(null);
  const [orgDropTarget, setOrgDropTarget] = useState(null); // { id, position: 'on' | 'before' | 'after' }
  const [orgConfigId, setOrgConfigId] = useState(null);
  const [orgConfigData, setOrgConfigData] = useState({});
  const [orgDragServiceKey, setOrgDragServiceKey] = useState(null); // "parentId-svcId"
  const [orgDropServiceKey, setOrgDropServiceKey] = useState(null);
  const [agregatsImporting, setAgregatsImporting] = useState(false);
  const [agregatsResult, setAgregatsResult] = useState(null);
  const [groupes, setGroupes] = useState([]);
  const [orgMetrics, setOrgMetrics] = useState({});
  const [orgMoisReels, setOrgMoisReels] = useState([]);
  const [orgChamps, setOrgChamps] = useState(() => {
    try { return JSON.parse(localStorage.getItem('finance_org_champs')) || []; } catch { return []; }
  });
  const [orgSurvol, setOrgSurvol] = useState(null);   // { id, x, y }
  const orgSurvolTimer = useRef(null);
  const [orgServiceMenu, setOrgServiceMenu] = useState(null); // { svcId, libelle, current, x, y, parentId, renaming?, libelleEdit? }
  const [orgBlocForm, setOrgBlocForm] = useState(null); // { parentId, libelle } — creation d'un bloc sous une carte
  const [orgUnattachedDrop, setOrgUnattachedDrop] = useState(false);
  const orgScrollRef = useRef(null);            // conteneur defilant de l'arbre
  const orgAutoScroll = useRef({ raf: null, x: 0, y: 0, actif: false });
  const orgDescendants = useRef(null);         // descendants du noeud glisse, calcules une fois
  const orgResizeRef = useRef(null);            // { svcId, axis, startX/Y, startWidth/Height, width, height }
  const [orgResizeSize, setOrgResizeSize] = useState(null); // apercu live { svcId, width, height }

  // Fiche contrat modal
  const [ficheContratSalarieId, setFicheContratSalarieId] = useState(null);
  const [ficheContratData, setFicheContratData] = useState({});
  const [ficheContratElements, setFicheContratElements] = useState([]);
  const [ficheNewElementTypeId, setFicheNewElementTypeId] = useState('');
  const [ficheNewElementMontant, setFicheNewElementMontant] = useState('');
  const [ficheSaving, setFicheSaving] = useState(false);

  // Sections collapsées par salarié : { "salarie_id-section_key": true }
  const [expandedSections, setExpandedSections] = useState({});

  const handleSortAnnuaire = (col) => {
    setSortAnnuaire(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  };

  const sortAnnuaireIcon = (col) => sortAnnuaire.col === col ? (sortAnnuaire.dir === 'asc' ? ' ▲' : ' ▼') : '';

  const handleSortDetail = (col) => setSortDetail(p => ({ col, dir: p.col === col && p.dir === 'asc' ? 'desc' : 'asc' }));
  const sortDetailIcon = (col) => (sortDetail.col === col ? (sortDetail.dir === 'asc' ? ' ▲' : ' ▼') : '');

  // Regroupement prealable : sans lui, chaque comparaison du tri refiltrerait
  // les milliers de lignes de detail.
  // Le selecteur porte soit une societe (« 5 »), soit un groupe (« g:2 »).
  // Un groupe se traduit par la liste des societes qu'il contient.
  const filtreSociete = useMemo(() => {
    const v = String(selectedSociete || '');
    if (!v) return {};
    if (v.startsWith('g:')) {
      const gid = Number(v.slice(2));
      const ids = societes.filter(so => so.groupe_id === gid).map(so => so.id);
      // Groupe vide : on ne renvoie aucun filtre plutot que de tout afficher
      return ids.length ? { societe_ids: ids.join(',') } : { societe_ids: '0' };
    }
    return { societe_id: v };
  }, [selectedSociete, societes]);

  const detailsParSalarie = useMemo(() => {
    const m = new Map();
    details.forEach(d => {
      if (!m.has(d.salarie_id)) m.set(d.salarie_id, []);
      m.get(d.salarie_id).push(d);
    });
    return m;
  }, [details]);

  // Le tri porte sur la valeur REELLEMENT affichee dans la colonne, c'est-a-dire
  // la somme des lignes de detail. Trier sur une autre grandeur donnerait un
  // ordre incomprehensible a la lecture.
  const valeurTriDetail = (s, col) => {
    if (col === 'nom') return `${s.nom} ${s.prenom || ''}`.trim().toLowerCase();
    const sD = detailsParSalarie.get(s.id) || [];
    if (col === 'total') return MOIS.reduce((acc, _, mi) => acc + sumDetailsForCalc(sD, mi + 1), 0);
    return sumDetailsForCalc(sD, col);
  };

  const toggleSection = (salarieId, sectionKey) => {
    const key = `${salarieId}-${sectionKey}`;
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // PDF import
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfInfos, setPdfInfos] = useState(null); // { parFichier, echecs, periodes, doublons }
  const [pdfStep, setPdfStep] = useState(null); // null → 'confirm-mois' → 'preview'
  const [pdfImportMois, setPdfImportMois] = useState(new Date().getMonth() + 1);
  const [pdfImportYear, setPdfImportYear] = useState(new Date().getFullYear());
  const [pdfImporting, setPdfImporting] = useState(false);
  const [pdfUnknowns, setPdfUnknowns] = useState({});
  const [pdfExpandedRows, setPdfExpandedRows] = useState(new Set());
  const [recapExpanded, setRecapExpanded] = useState({}); // { "act-X": true, "poste-X": true }

  const showToast = useCallback((msg, type = 'success', undoAction = null) => {
    setToast({ msg, type, undoAction });
    if (!undoAction) {
      setTimeout(() => setToast(null), 3000);
    } else {
      setTimeout(() => setToast(null), 8000);
    }
  }, []);

  const handleStartEditAnnuaire = (s) => {
    setEditingAnnuaire(s.id);
    setEditAnnuaireVal({
      nom: s.nom || '', prenom: s.prenom || '',
      numero_salarie: s.numero_salarie || '',
      societe_id: s.societe_id || '',
      activite_id: s.activite_id || '',
      poste_id: s.poste_id || '',
      date_anciennete: s.date_anciennete ? s.date_anciennete.substring(0, 10) : '',
      qualification: s.qualification || '',
      qualification_interne: s.qualification_interne || '',
      date_fin_contrat: s.date_fin_contrat ? s.date_fin_contrat.substring(0, 10) : '',
      motif_fin_contrat: s.motif_fin_contrat || '',
      superieur_id: s.superieur_id || '',
      service_id: s.service_id || '',
    });
  };

  const handleSaveAnnuaire = async () => {
    try {
      await salairesApi.updateSalarie(editingAnnuaire, editAnnuaireVal);
      setEditingAnnuaire(null);
      showToast('Salarie mis a jour');
      const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
      const sal = await salairesApi.getSalaries(params);
      setSalaries(sal.data);
    } catch { showToast('Erreur sauvegarde', 'error'); }
  };

  // ── Fiche contrat ────────────────────────────────────────────────────
  const loadFicheContrat = async (salarieId) => {
    const s = salaries.find(x => x.id === salarieId);
    if (!s) return;
    setFicheContratData({
      statut_contrat: s.statut_contrat || '',
      temps_travail: s.temps_travail || '',
      salaire_brut_contractuel: s.salaire_brut_contractuel || '',
      variable_contractuel: s.variable_contractuel || '',
    });
    try {
      const res = await salairesApi.getContratElements({ salarie_id: salarieId });
      setFicheContratElements(res.data);
    } catch { setFicheContratElements([]); }
    setFicheNewElementTypeId('');
    setFicheNewElementMontant('');
  };

  const openFicheContrat = (salarieId) => {
    setFicheContratSalarieId(salarieId);
    loadFicheContrat(salarieId);
  };

  const saveFicheContrat = async () => {
    if (!ficheContratSalarieId) return;
    setFicheSaving(true);
    try {
      await salairesApi.updateSalarie(ficheContratSalarieId, {
        statut_contrat: ficheContratData.statut_contrat || null,
        temps_travail: ficheContratData.temps_travail || null,
        salaire_brut_contractuel: ficheContratData.salaire_brut_contractuel || null,
        variable_contractuel: ficheContratData.variable_contractuel || null,
      });
      showToast('Contrat sauvegarde');
      const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
      const sal = await salairesApi.getSalaries(params);
      setSalaries(sal.data);
    } catch { showToast('Erreur sauvegarde contrat', 'error'); }
    setFicheSaving(false);
  };

  const ficheFilteredList = salaries.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      return (s.nom || '').toLowerCase().includes(q) || (s.prenom || '').toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => {
    const col = sortAnnuaire.col;
    let va = a[col], vb = b[col];
    if (va == null) va = '';
    if (vb == null) vb = '';
    if (col === 'date_anciennete' || col === 'date_fin_contrat') {
      va = va ? new Date(va).getTime() : 0;
      vb = vb ? new Date(vb).getTime() : 0;
    } else {
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
    }
    if (va < vb) return sortAnnuaire.dir === 'asc' ? -1 : 1;
    if (va > vb) return sortAnnuaire.dir === 'asc' ? 1 : -1;
    return 0;
  });

  const ficheCurrentIndex = ficheContratSalarieId ? ficheFilteredList.findIndex(s => s.id === ficheContratSalarieId) : -1;
  const ficheCurrent = ficheCurrentIndex >= 0 ? ficheFilteredList[ficheCurrentIndex] : null;

  const navigateFiche = async (direction) => {
    const nextIndex = ficheCurrentIndex + direction;
    if (nextIndex < 0 || nextIndex >= ficheFilteredList.length) return;
    await saveFicheContrat();
    const nextId = ficheFilteredList[nextIndex].id;
    setFicheContratSalarieId(nextId);
    loadFicheContrat(nextId);
  };

  const handleAddContratElement = async () => {
    if (!ficheNewElementTypeId || !ficheContratSalarieId) return;
    try {
      await salairesApi.saveContratElement({
        salarie_id: ficheContratSalarieId,
        type_charge_id: parseInt(ficheNewElementTypeId),
        montant: parseFloat(ficheNewElementMontant) || 0,
      });
      const res = await salairesApi.getContratElements({ salarie_id: ficheContratSalarieId });
      setFicheContratElements(res.data);
      setFicheNewElementTypeId('');
      setFicheNewElementMontant('');
    } catch { showToast('Erreur ajout element', 'error'); }
  };

  const handleDeleteContratElement = async (elementId) => {
    try {
      await salairesApi.deleteContratElement(elementId);
      setFicheContratElements(prev => prev.filter(e => e.id !== elementId));
    } catch { showToast('Erreur suppression', 'error'); }
  };

  const handleUpdateContratElementMontant = async (element, montant) => {
    try {
      await salairesApi.saveContratElement({
        salarie_id: ficheContratSalarieId,
        type_charge_id: element.type_charge_id,
        montant: parseFloat(montant) || 0,
      });
      const res = await salairesApi.getContratElements({ salarie_id: ficheContratSalarieId });
      setFicheContratElements(res.data);
    } catch { showToast('Erreur mise a jour', 'error'); }
  };

  // ── Config organigramme ──────────────────────────────────────────
  const openOrgConfig = (salarieId) => {
    const s = salaries.find(x => x.id === salarieId);
    if (!s) return;
    setOrgConfigId(salarieId);
    setOrgConfigData({
      couleur_org: s.couleur_org || '',
      service_id: s.service_id || '',
      orientation_org: s.orientation_org || 'horizontal',
    });
  };

  const saveOrgConfig = async () => {
    if (!orgConfigId) return;
    try {
      const updates = { couleur_org: orgConfigData.couleur_org || null, service_id: orgConfigData.service_id || null, orientation_org: orgConfigData.orientation_org || 'horizontal' };
      await salairesApi.updateSalarie(orgConfigId, updates);
      showToast('Config organigramme sauvegardee');
      const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
      const sal = await salairesApi.getSalaries(params);
      setSalaries(sal.data);
      salairesApi.getSalaries({ ...filtreSociete }).then(r => setAllSalaries(r.data)).catch(() => {});
    } catch { showToast('Erreur sauvegarde', 'error'); }
  };

  const reloadSalaries = async () => {
    const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
    const sal = await salairesApi.getSalaries(params);
    setSalaries(sal.data);
    salairesApi.getSalaries({ ...filtreSociete }).then(r => setAllSalaries(r.data)).catch(() => {});
  };

  // Supprime le rattachement hierarchique. Le salarie retombe dans "Non rattaches"
  // s'il n'a aucun N-1, sinon il devient une racine de l'organigramme.
  const detacherSuperieur = async (salarieId, pendingOrgConfig = null) => {
    try {
      const updates = { superieur_id: null };
      // On embarque les modifs en cours de la modale pour ne pas les perdre a la fermeture
      if (pendingOrgConfig) {
        updates.couleur_org = pendingOrgConfig.couleur_org || null;
        updates.service_id = pendingOrgConfig.service_id || null;
        updates.orientation_org = pendingOrgConfig.orientation_org || 'horizontal';
      }
      const ancienSup = salaries.find(x => x.id === salarieId)?.superieur_id || null;
      await salairesApi.updateSalarie(salarieId, updates);
      showToast('Lien hierarchique supprime', 'success', async () => {
        try { await salairesApi.updateSalarie(salarieId, { superieur_id: ancienSup }); await reloadSalaries(); }
        catch { showToast('Erreur annulation', 'error'); }
      });
      await reloadSalaries();
    } catch { showToast('Erreur detachement', 'error'); }
  };

  // Masque / reaffiche un salarie dans l'organigramme. Non destructif : il reste
  // actif, la paie et le reporting ne bougent pas.
  const masquerOrganigramme = async (salarieId, masquer, pendingOrgConfig = null) => {
    try {
      const updates = { exclure_organigramme: masquer };
      if (pendingOrgConfig) {
        updates.couleur_org = pendingOrgConfig.couleur_org || null;
        updates.service_id = pendingOrgConfig.service_id || null;
        updates.orientation_org = pendingOrgConfig.orientation_org || 'horizontal';
      }
      await salairesApi.updateSalarie(salarieId, updates);
      showToast(masquer ? 'Masque de l\'organigramme' : 'Reaffiche dans l\'organigramme', 'success', async () => {
        try { await salairesApi.updateSalarie(salarieId, { exclure_organigramme: !masquer }); await reloadSalaries(); }
        catch { showToast('Erreur annulation', 'error'); }
      });
      await reloadSalaries();
    } catch { showToast('Erreur', 'error'); }
  };

  // ── Blocs de l'organigramme (services ancres sur un N+1) ──────────────
  // Creation en un clic depuis une carte : le bloc s'affiche vide sous le
  // N+1 (ancre org_parent_salarie_id), on y glisse ensuite des collaborateurs.
  const creerBloc = async () => {
    const libelle = (orgBlocForm?.libelle || '').trim();
    const parentId = orgBlocForm?.parentId;
    setOrgBlocForm(null);
    if (!libelle || !parentId) return;
    try {
      // Un service homonyme existant est reutilise (pas de doublon au referentiel)
      const existant = services.find(sv => sv.libelle.trim().toLowerCase() === libelle.toLowerCase());
      if (existant) await plApi.updateService(existant.id, { org_parent_salarie_id: parentId });
      else await plApi.createService({ libelle, org_parent_salarie_id: parentId });
      const svRes = await plApi.getServices();
      setServices(svRes.data);
      showToast(`Bloc « ${libelle} » cree — glissez-y des collaborateurs`);
    } catch { showToast('Erreur creation du bloc', 'error'); }
  };

  const renommerBloc = async (svcId, libelle) => {
    const nouveau = (libelle || '').trim();
    setOrgServiceMenu(null);
    if (!nouveau) return;
    try {
      await plApi.updateService(svcId, { libelle: nouveau });
      const svRes = await plApi.getServices();
      setServices(svRes.data);
      await reloadSalaries();
      showToast('Bloc renomme');
    } catch { showToast('Erreur renommage', 'error'); }
  };

  // Dissout le bloc affiche sous ce N+1 : ses membres restent rattaches au
  // N+1, ils sortent simplement du service. Le service est desactive s'il
  // n'est plus utilise nulle part ailleurs.
  const dissoudreBloc = async (svcId, parentId, libelle) => {
    setOrgServiceMenu(null);
    try {
      const membres = salaries.filter(s => String(s.service_id) === String(svcId) && s.superieur_id === parentId);
      for (const mb of membres) await salairesApi.updateSalarie(mb.id, { service_id: null });
      const encoreUtilise = salaries.some(s => String(s.service_id) === String(svcId) && s.superieur_id !== parentId);
      if (encoreUtilise) await plApi.updateService(svcId, { org_parent_salarie_id: null });
      else await plApi.deleteService(svcId);
      const svRes = await plApi.getServices();
      setServices(svRes.data);
      await reloadSalaries();
      showToast(`Bloc « ${libelle} » supprime${membres.length ? ' — les membres restent rattaches au N+1' : ''}`);
    } catch { showToast('Erreur suppression du bloc', 'error'); }
  };

  // Remet une dimension en automatique : champ = 'largeur_org' | 'hauteur_org'
  const setServiceTailleAuto = async (svcId, champ) => {
    try {
      await plApi.updateService(svcId, { [champ]: null });
      const svRes = await plApi.getServices();
      setServices(svRes.data);
      showToast(champ === 'largeur_org' ? 'Largeur automatique retablie' : 'Hauteur automatique retablie');
    } catch { showToast('Erreur redimensionnement', 'error'); }
    setOrgServiceMenu(null);
  };

  useEffect(() => {
    localStorage.setItem('finance_org_champs', JSON.stringify(orgChamps));
  }, [orgChamps]);

  useEffect(() => {
    if (activeView !== 'organigramme' || !isAdmin) return;
    salairesApi.getOrgMetrics()
      .then(r => { setOrgMetrics(r.data.metrics || {}); setOrgMoisReels(r.data.mois_reels || []); })
      .catch(() => {});
    if (isAdmin) salairesApi.getLicenciements().then(r => setLicenciements(r.data)).catch(() => {});
  }, [activeView, isAdmin]);

  // Effectif et masse salariale cumules par branche. Memoise et calcule au niveau
  // du composant car la fiche au survol est rendue hors de l'arbre.
  // Memes regles de visibilite que l'arbre : contrats echus et masques exclus,
  // les N-1 d'un masque remontant au premier N+1 visible.
  const orgAgregats = useMemo(() => {
    const today = new Date().toISOString().substring(0, 10);
    const actifs = salaries.filter(s => !s.date_fin_contrat || s.date_fin_contrat.substring(0, 10) >= today);
    const masques = new Set(actifs.filter(s => s.exclure_organigramme).map(s => s.id));
    const supDe = {};
    actifs.forEach(s => { supDe[s.id] = s.superieur_id || null; });
    const parentVisible = (s) => {
      let p = s.superieur_id || null;
      const vus = new Set();
      while (p && masques.has(p) && !vus.has(p)) { vus.add(p); p = supDe[p] || null; }
      return p && !masques.has(p) ? p : null;
    };
    const visibles = actifs.filter(s => !masques.has(s.id));
    const enfants = {};
    visibles.forEach(s => { const p = parentVisible(s); if (p) (enfants[p] = enfants[p] || []).push(s.id); });
    const res = {};
    const calc = (id) => {
      if (res[id]) return res[id];
      const m = orgMetrics[id];
      let effectif = 1;
      let masse = (m && m.cout_employeur) || 0;
      (enfants[id] || []).forEach(c => { const a = calc(c); effectif += a.effectif; masse += a.masse; });
      const a = { effectif, masse, directs: (enfants[id] || []).length };
      res[id] = a;
      return a;
    };
    visibles.forEach(s => calc(s.id));
    return res;
  }, [salaries, orgMetrics]);

  // Montants et absences (donnee de sante) : admins uniquement
  const champsAutorises = useMemo(() => ORG_CHAMPS.filter(c => isAdmin || !c.sensible), [isAdmin]);

  const basculerChamp = (cle) => setOrgChamps(c => (c.includes(cle) ? c.filter(x => x !== cle) : [...c, cle]));

  // Survol : petit delai pour ne pas clignoter en balayant les cartes,
  // et jamais pendant un glisser-deposer.
  const survolerCarte = (e, id) => {
    if (orgDragId) return;
    const x = e.clientX, y = e.clientY;
    clearTimeout(orgSurvolTimer.current);
    orgSurvolTimer.current = setTimeout(() => setOrgSurvol({ id, x, y }), 350);
  };
  const quitterCarte = () => { clearTimeout(orgSurvolTimer.current); setOrgSurvol(null); };

  // Le glisser-deposer HTML5 ne fait pas defiler un conteneur : on s'en charge.
  // Sans cela, toute cible hors de l'ecran est inatteignable — cas majoritaire
  // sur un organigramme large de plusieurs milliers de pixels.
  const boucleAutoScroll = useCallback(() => {
    const el = orgScrollRef.current;
    const st = orgAutoScroll.current;
    if (!el || !st.actif) { st.raf = null; return; }
    const r = el.getBoundingClientRect();
    const MARGE = 100, VITESSE_MAX = 24;
    const pousse = (pos, min, max) => {
      if (pos < min + MARGE) return -VITESSE_MAX * Math.min(1, (min + MARGE - pos) / MARGE);
      if (pos > max - MARGE) return VITESSE_MAX * Math.min(1, (pos - (max - MARGE)) / MARGE);
      return 0;
    };
    const dx = pousse(st.x, r.left, r.right);
    const dy = pousse(st.y, r.top, r.bottom);
    if (dx) el.scrollLeft += dx;
    if (dy) el.scrollTop += dy;
    st.raf = requestAnimationFrame(boucleAutoScroll);
  }, []);

  // Capture : les gestionnaires des cartes appellent stopPropagation en phase bouillonnante
  const surveillerAutoScroll = useCallback((e) => {
    const st = orgAutoScroll.current;
    st.x = e.clientX; st.y = e.clientY; st.actif = true;
    if (!st.raf) st.raf = requestAnimationFrame(boucleAutoScroll);
  }, [boucleAutoScroll]);

  const arreterAutoScroll = useCallback(() => {
    const st = orgAutoScroll.current;
    st.actif = false;
    if (st.raf) { cancelAnimationFrame(st.raf); st.raf = null; }
  }, []);

  useEffect(() => {
    // dragend part de la source, drop de la cible : on coupe dans les deux cas
    window.addEventListener('dragend', arreterAutoScroll);
    window.addEventListener('drop', arreterAutoScroll);
    return () => {
      window.removeEventListener('dragend', arreterAutoScroll);
      window.removeEventListener('drop', arreterAutoScroll);
      arreterAutoScroll();
    };
  }, [arreterAutoScroll]);

  // Le redimensionnement se poursuit hors du bloc : on ecoute la fenetre entiere
  useEffect(() => {
    const LARGEUR_MIN = 154; // une carte (130) + marges internes du bloc
    const HAUTEUR_MIN = 60;  // le libelle du service reste lisible
    const onMove = (e) => {
      const r = orgResizeRef.current;
      if (!r) return;
      if (r.axis.includes('x')) r.width = Math.max(LARGEUR_MIN, Math.round(r.startWidth + (e.clientX - r.startX)));
      if (r.axis.includes('y')) r.height = Math.max(HAUTEUR_MIN, Math.round(r.startHeight + (e.clientY - r.startY)));
      setOrgResizeSize({ svcId: r.svcId, width: r.width, height: r.height });
    };
    const onUp = async () => {
      const r = orgResizeRef.current;
      if (!r) return;
      orgResizeRef.current = null;
      setOrgResizeSize(null);
      const payload = {};
      if (r.axis.includes('x') && r.width !== r.startWidth) payload.largeur_org = r.width;
      if (r.axis.includes('y') && r.height !== r.startHeight) payload.hauteur_org = r.height;
      if (!Object.keys(payload).length) return;
      try {
        await plApi.updateService(r.svcId, payload);
        const svRes = await plApi.getServices();
        setServices(svRes.data);
      } catch { showToast('Erreur redimensionnement', 'error'); }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [showToast]);

  // orientation = 'horizontal' | 'vertical' | '' (vide = heriter du N+1)
  const setServiceOrientation = async (svcId, orientation) => {
    try {
      await plApi.updateService(svcId, { orientation_org: orientation || null });
      const svRes = await plApi.getServices();
      setServices(svRes.data);
      showToast('Orientation du service mise a jour');
    } catch { showToast('Erreur orientation', 'error'); }
    setOrgServiceMenu(null);
  };

  const handleAnnuairePdfUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setAnnuairePdfImporting(true);
    try {
      let allBulletins = [];
      for (const file of files) {
        const bulletins = await parseBulletinPdf(file);
        allBulletins.push(...bulletins);
      }
      if (allBulletins.length === 0) { showToast('Aucun bulletin detecte', 'error'); return; }

      // Dédupliquer par numero_salarie (garder le dernier)
      const byNum = {};
      for (const b of allBulletins) {
        byNum[b.numero_salarie] = b;
      }

      // Enrichir avec le matching salarié existant et la société
      const preview = Object.values(byNum).map(b => {
        const existing = salaries.find(s => s.numero_salarie === b.numero_salarie);
        const matchedSociete = societes.find(s => b.societe && s.nom.toLowerCase().includes(b.societe.split(' ')[0].toLowerCase()));
        return {
          ...b,
          salarie_id: existing?.id || null,
          existing_nom: existing ? `${existing.nom} ${existing.prenom}` : null,
          existing_societe: existing?.societe_nom || null,
          existing_qualification: existing?.qualification || null,
          existing_date_anciennete: existing?.date_anciennete || null,
          societe_id: matchedSociete?.id || existing?.societe_id || null,
          societe_matched: matchedSociete?.nom || null,
          will_update: !!existing,
        };
      });
      setAnnuairePdfPreview(preview);
    } catch (err) {
      showToast('Erreur lecture PDF: ' + (err.message || ''), 'error');
    } finally {
      setAnnuairePdfImporting(false);
      e.target.value = '';
    }
  };

  const handleAnnuairePdfConfirm = async () => {
    if (!annuairePdfPreview) return;
    setAnnuairePdfImporting(true);
    let updated = 0, skipped = 0;
    try {
      for (const b of annuairePdfPreview) {
        if (!b.salarie_id) { skipped++; continue; }
        const data = {};
        if (b.societe_id) data.societe_id = b.societe_id;
        if (b.qualification) data.qualification = b.qualification;
        if (b.date_anciennete) data.date_anciennete = b.date_anciennete;
        if (Object.keys(data).length === 0) { skipped++; continue; }
        await salairesApi.updateSalarie(b.salarie_id, data);
        updated++;
      }
      showToast(`${updated} salarie(s) mis a jour` + (skipped ? `, ${skipped} ignore(s)` : ''));
      setAnnuairePdfPreview(null);
      const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
      const sal = await salairesApi.getSalaries(params);
      setSalaries(sal.data);
    } catch { showToast('Erreur import', 'error'); }
    finally { setAnnuairePdfImporting(false); }
  };

  // Load filters
  useEffect(() => {
    plApi.getGroupes().then(r => setGroupes(r.data)).catch(() => {});
    Promise.all([plApi.getExercices(), plApi.getSocietes(), plApi.getActivites(), plApi.getPostes(), salairesApi.getTypesCharges(), salairesApi.getCoefficient(), plApi.getServices()])
      .then(([ex, so, ac, po, tc, co, sv]) => {
        setExercices(ex.data);
        setSocietes(so.data);
        setActivites(ac.data);
        setPostes(po.data);
        setTypesCharges(tc.data);
        setServices(sv.data);
        setCoeffDefault(co.data.coeff || 1.45);
        if (ex.data.length > 0) setSelectedExercice(ex.data[0].id);
      }).catch(() => showToast('Erreur chargement', 'error'));
  }, [showToast]);

  // Load data
  useEffect(() => {
    if (!selectedExercice) return;
    setLoading(true);
    const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
    Promise.all([
      salairesApi.getSalaries(params),
      salairesApi.getDetails(params),
      salairesApi.getDashboard({ exercice_id: selectedExercice, ...filtreSociete }),
      salairesApi.getTotalVerse({ exercice_id: selectedExercice }),
      salairesApi.getMoisType({ exercice_id: selectedExercice }),
    ]).then(([sal, det, dash, tv, mt]) => {
      setSalaries(sal.data);
      setDetails(det.data);
      setDashboard(dash.data);
      setTotalVerse(tv.data);
      setMoisType(mt.data);
      // Salariés groupés par défaut
      setExpandedSalaries({});
    }).catch(() => showToast('Erreur chargement', 'error'))
      .finally(() => setLoading(false));
    salairesApi.getLicenciements().then(r => setLicenciements(r.data)).catch(() => {});
    salairesApi.getSalaries({ ...filtreSociete }).then(r => setAllSalaries(r.data)).catch(() => {});
  }, [selectedExercice, selectedSociete, selectedActivite, search, showToast]);

  const refreshLicenciements = () => salairesApi.getLicenciements().then(r => setLicenciements(r.data));

  const handleSaveDetail = async (salarie_id, mois, type_charge_id) => {
    try {
      await salairesApi.saveDetail({
        salarie_id, exercice_id: selectedExercice, mois, type_charge_id,
        montant: parseFloat(editingValue) || 0
      });
      setEditingCell(null);
      showToast('Enregistre');
      // Refresh
      const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined };
      const det = await salairesApi.getDetails(params);
      setDetails(det.data);
    } catch { showToast('Erreur', 'error'); }
  };

  const handleAddSalarie = async () => {
    if (!newSalarie.nom) return;
    try {
      await salairesApi.createSalarie(newSalarie);
      setNewSalarie({ nom: '', prenom: '', societe_id: '', activite_id: '' });
      setShowAdd(false);
      showToast('Salarie ajoute');
      const params = { exercice_id: selectedExercice, ...filtreSociete };
      const sal = await salairesApi.getSalaries(params);
      setSalaries(sal.data);
    } catch { showToast('Erreur', 'error'); }
  };

  const handleSaveName = async (id) => {
    if (!editNameVal.nom.trim()) { showToast('Nom obligatoire', 'error'); return; }
    try {
      await salairesApi.updateSalarie(id, { nom: editNameVal.nom.trim(), prenom: editNameVal.prenom.trim() });
      setEditingName(null);
      showToast('Nom mis a jour');
      const params = { exercice_id: selectedExercice, ...filtreSociete };
      const sal = await salairesApi.getSalaries(params);
      setSalaries(sal.data);
    } catch { showToast('Erreur', 'error'); }
  };

  const [purgeMois, setPurgeMois] = useState('');

  const handlePurgeMois = async () => {
    if (!purgeMois || !selectedExercice) return;
    const moisLabel = MOIS[parseInt(purgeMois) - 1];
    if (!purgeConfirm || purgeConfirm.mois !== purgeMois) {
      setPurgeConfirm({ mois: purgeMois, label: moisLabel });
      return;
    }
    setPurgeConfirm(null);
    try {
      const params = { exercice_id: selectedExercice, mois: purgeMois };
      if (selectedSociete) params.societe_id = selectedSociete;
      if (selectedActivite !== 'global') params.activite_id = selectedActivite;
      const res = await salairesApi.deleteDetailsBulk(params);
      showToast(`${res.data.deleted} charge(s) et ${res.data.deletedTv} total(s) verse supprimes`);
      setPurgeMois('');
      const refreshParams = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined };
      const [sal, det, dash, tv] = await Promise.all([
        salairesApi.getSalaries(refreshParams),
        salairesApi.getDetails(refreshParams),
        salairesApi.getDashboard({ exercice_id: selectedExercice, ...filtreSociete }),
        salairesApi.getTotalVerse({ exercice_id: selectedExercice }),
      ]);
      setSalaries(sal.data);
      setDetails(det.data);
      setDashboard(dash.data);
      setTotalVerse(tv.data);
    } catch { showToast('Erreur suppression', 'error'); }
  };

  const handleDeleteSalarie = async (id) => {
    try {
      await salairesApi.deleteSalarie(id);
      const params = { exercice_id: selectedExercice, ...filtreSociete };
      const sal = await salairesApi.getSalaries(params);
      setSalaries(sal.data);
      showToast('Salarie desactive');
    } catch { showToast('Erreur', 'error'); }
  };

  const handleExport = async () => {
    try {
      const res = await salairesApi.exportExcel({ exercice_id: selectedExercice, ...filtreSociete });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'masse-salariale.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { showToast('Erreur export', 'error'); }
  };

  const handleAgregatsUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const purge = window.confirm(
      "Purger TOUS les details existants avant l'import ?\n\n" +
      "OK = repartir d'une base vierge (recommande pour le premier import Excel,\n" +
      "cela efface les lignes issues des anciens bulletins PDF).\n" +
      "Annuler = ne remplacer que les mois presents dans le fichier."
    );
    setAgregatsImporting(true);
    setAgregatsResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purge_totale', String(purge));
      const r = await salairesApi.importAgregats(fd);
      setAgregatsResult(r.data);
      showToast(`${r.data.lignes_inserees} lignes importees`);
      // L'import cree les types de charge manquants (un par agregat) : sans ce
      // rechargement, le composant garde sa liste d'origine et les nouvelles
      // lignes s'affichent sans libelle jusqu'au prochain rafraichissement.
      const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined };
      const [det, tcs] = await Promise.all([
        salairesApi.getDetails(params),
        salairesApi.getTypesCharges(),
      ]);
      setDetails(det.data);
      setTypesCharges(tcs.data);
    } catch (err) {
      const st = err.response?.status;
      if (err.response?.data?.diagnostic) setAgregatsResult({ ...err.response.data, echec: true });
      const msg = err.response?.data?.error
        || (st === 413 ? 'Fichier trop volumineux : le serveur le refuse avant lecture' : null)
        || (st === 504 ? 'Le serveur a mis trop de temps a repondre' : null)
        || (st ? `Erreur import (HTTP ${st})` : 'Erreur import : serveur injoignable');
      showToast(msg, 'error');
    }
    setAgregatsImporting(false);
  };

  // Plusieurs PDF a la fois : une entite = un fichier, un seul mois pour le lot.
  const handlePdfUpload = async (e) => {
    const fichiers = Array.from(e.target.files || []);
    e.target.value = '';
    if (fichiers.length === 0) return;
    if (!selectedExercice) { showToast('Selectionnez un exercice', 'error'); return; }

    setPdfImporting(true);
    setPdfInfos(null);
    try {
      const bulletins = [];
      const echecs = [];
      const parFichier = [];
      for (const f of fichiers) {
        try {
          const lus = await parseBulletinPdf(f);
          if (lus.length === 0) echecs.push(`${f.name} : aucun bulletin detecte`);
          else {
            bulletins.push(...lus.map(b => ({ ...b, _fichier: f.name })));
            parFichier.push({ nom: f.name, bulletins: lus.length });
          }
        } catch (err) {
          echecs.push(`${f.name} : ${err.message || 'format illisible'}`);
        }
      }
      if (bulletins.length === 0) {
        showToast(echecs[0] || 'Aucun bulletin detecte', 'error');
        setPdfInfos({ parFichier, echecs, periodes: [], doublons: [] });
        return;
      }

      // Periode : le lot partage un seul mois, on verifie qu'ils concordent
      const periodeDe = (b) => {
        const m = String(b.periode || '').match(/(\d{2})\/(\d{2})\/(\d{2})/);
        return m ? `${m[2]}/${m[3]}` : null;
      };
      const periodes = [...new Set(bulletins.map(periodeDe).filter(Boolean))];
      if (periodes.length > 0) {
        const [mm, yy] = periodes[0].split('/').map(Number);
        setPdfImportMois(mm);
        setPdfImportYear(yy < 100 ? 2000 + yy : yy);
      }

      // Un meme matricule present dans deux fichiers ecraserait le premier
      const vus = new Map();
      const doublons = [];
      bulletins.forEach(b => {
        if (!b.numero_salarie) return;
        if (vus.has(b.numero_salarie)) doublons.push(`${b.numero_salarie} (${vus.get(b.numero_salarie)} et ${b._fichier})`);
        else vus.set(b.numero_salarie, b._fichier);
      });

      setPdfInfos({ parFichier, echecs, periodes, doublons });
      setPdfPreview(bulletins);
      setPdfStep('confirm-mois');
    } catch (err) {
      showToast('Erreur lecture PDF: ' + (err.message || 'format invalide'), 'error');
    } finally {
      setPdfImporting(false);
    }
  };

  const handlePdfContinueToPreview = () => {
    // Identifier les salariés inconnus
    const unknowns = {};
    for (const b of pdfPreview) {
      const known = salaries.find(s => s.numero_salarie === b.numero_salarie);
      if (!known) {
        unknowns[b.numero_salarie] = { nom: b.nom || '', prenom: b.prenom || '' };
      }
    }
    setPdfUnknowns(unknowns);
    setPdfExpandedRows(new Set());
    setPdfStep('preview');
  };

  const handlePdfCancel = () => {
    setPdfPreview(null);
    setPdfStep(null);
    setPdfInfos(null);
  };

  const handlePdfConfirm = async () => {
    // Vérifier que tous les inconnus ont un nom
    const missingNames = Object.entries(pdfUnknowns).filter(([, v]) => !v.nom.trim());
    if (missingNames.length > 0) { showToast('Renseignez le nom de chaque nouveau salarie', 'error'); return; }

    setPdfImporting(true);
    try {
      const exercice = exercices.find(ex => ex.annee === pdfImportYear);
      if (!exercice) { showToast(`Exercice ${pdfImportYear} introuvable`, 'error'); setPdfImporting(false); return; }
      const payload = {
        exercice_id: exercice.id,
        mois: pdfImportMois,
        bulletins: pdfPreview.map(({ _fichier, ...b }) => ({
          ...b,
          nom: pdfUnknowns[b.numero_salarie]?.nom || null,
          prenom: pdfUnknowns[b.numero_salarie]?.prenom || null,
        })),
      };
      const res = await salairesApi.importPdf(payload);
      setImportResult(res.data);
      showToast(res.data.message);
      setPdfPreview(null);
      setPdfStep(null);

      // Refresh
      const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined };
      const [sal, det, dash, tv] = await Promise.all([
        salairesApi.getSalaries(params),
        salairesApi.getDetails(params),
        salairesApi.getDashboard({ exercice_id: selectedExercice, ...filtreSociete }),
        salairesApi.getTotalVerse({ exercice_id: selectedExercice }),
      ]);
      setSalaries(sal.data);
      setDetails(det.data);
      setDashboard(dash.data);
      setTotalVerse(tv.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur import PDF', 'error');
    } finally {
      setPdfImporting(false);
    }
  };

  const togglePdfRow = (numero) => {
    setPdfExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(numero)) next.delete(numero); else next.add(numero);
      return next;
    });
  };

  const handleSaveTotalVerse = async (salarie_id, mois) => {
    try {
      await salairesApi.saveTotalVerse({
        salarie_id, exercice_id: selectedExercice, mois,
        montant: parseFloat(editingValue) || 0
      });
      setEditingCell(null);
      showToast('Total verse enregistre');
      const tv = await salairesApi.getTotalVerse({ exercice_id: selectedExercice });
      setTotalVerse(tv.data);
    } catch { showToast('Erreur', 'error'); }
  };

  // Helper : type de mois (budget/reel) — global par mois
  const getMoisTypeFn = (mois) => {
    const row = moisType.find(m => m.mois === mois);
    return row?.type_donnee || 'budget';
  };

  const handleToggleMoisType = async (mois) => {
    const current = getMoisTypeFn(mois);
    const newType = current === 'reel' ? 'budget' : 'reel';
    try {
      await salairesApi.saveMoisType({ exercice_id: selectedExercice, mois, type_donnee: newType });
      const mt = await salairesApi.getMoisType({ exercice_id: selectedExercice });
      setMoisType(mt.data);
    } catch { showToast('Erreur', 'error'); }
  };

  const handleExtendPrevisions = async (salarieId) => {
    const moisSource = parseInt(projeterMoisSource);
    if (!moisSource) { showToast('Selectionnez un mois source', 'error'); return; }
    try {
      const res = await salairesApi.extendPrevisions({ salarie_id: salarieId, exercice_id: selectedExercice, mois_source: moisSource });
      showToast(`${res.data.copiedMonths} mois projete(s)${res.data.skippedReel > 0 ? `, ${res.data.skippedReel} mois reel(s) preserves` : ''}`);
      setProjecterFor(null);
      setProjecterMoisSource('');
      const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
      const [det, tv] = await Promise.all([
        salairesApi.getDetails(params),
        salairesApi.getTotalVerse({ exercice_id: selectedExercice }),
      ]);
      setDetails(det.data);
      setTotalVerse(tv.data);
    } catch (err) { showToast(err.response?.data?.error || 'Erreur projection', 'error'); }
  };

  const handleSaveCoeff = async () => {
    const val = parseFloat(coeffInput);
    if (!val || val < 1 || val > 3) { showToast('Coefficient entre 1.0 et 3.0', 'error'); return; }
    try {
      await salairesApi.saveCoefficient(val);
      setCoeffDefault(val);
      setShowCoeffModal(false);
      showToast('Coefficient mis a jour');
    } catch { showToast('Erreur', 'error'); }
  };

  // IDs des types de charges exclues du calcul total
  const excludedTcIds = new Set(typesCharges.filter(t => t.exclure_calcul).map(t => t.id));

  // Somme des détails d'un salarié pour un mois, en excluant les charges marquées exclure_calcul
  const sumDetailsForCalc = (salarieDetails, mois) => {
    return salarieDetails
      .filter(d => d.mois === mois && !excludedTcIds.has(d.type_charge_id))
      .reduce((acc, d) => acc + (parseFloat(d.montant) || 0), 0);
  };

  // Calcul du ratio pour un collaborateur (depuis ses mois reels)
  const getAutresRatio = (salarieId) => {
    const sD = details.filter(d => d.salarie_id === salarieId);
    const tvRows = totalVerse.filter(tv => tv.salarie_id === salarieId && parseFloat(tv.montant) > 0);
    if (tvRows.length === 0) return coeffDefault;
    let totalTV = 0, totalLignes = 0;
    tvRows.forEach(tv => {
      const sommeLignes = sumDetailsForCalc(sD, tv.mois);
      if (sommeLignes > 0) {
        totalTV += parseFloat(tv.montant);
        totalLignes += sommeLignes;
      }
    });
    return totalLignes > 0 ? totalTV / totalLignes : coeffDefault;
  };

  const handleAddChargeLine = async (salarieId) => {
    if (!newChargeTypeId || !selectedExercice) return;
    // Inserer un montant 0 pour le mois 1 afin de creer la ligne
    try {
      await salairesApi.saveDetail({
        salarie_id: salarieId,
        exercice_id: selectedExercice,
        mois: 1,
        type_charge_id: parseInt(newChargeTypeId),
        montant: 0
      });
      setAddingChargeFor(null);
      setNewChargeTypeId('');
      showToast('Ligne ajoutee');
      // Refresh details
      const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined };
      const det = await salairesApi.getDetails(params);
      setDetails(det.data);
    } catch { showToast('Erreur', 'error'); }
  };

  const getSalarieTotal = (salarieId) => {
    return details.filter(d => d.salarie_id === salarieId).reduce((s, d) => s + (parseFloat(d.montant) || 0), 0);
  };

  return (
    <div className="salaires-container">
      {toast && (
        <div className={`pl-toast ${toast.type}`}>
          {toast.msg}
          {toast.undoAction && (
            <button className="toast-undo-btn" onClick={() => { toast.undoAction(); setToast(null); }}>
              Annuler
            </button>
          )}
        </div>
      )}

      {/* Dashboard stats */}
      {!isOrgMode && dashboard && (
        <div className="salaires-stats">
          <div className="stat-card">
            <div className="stat-value">{dashboard.nbSalaries}</div>
            <div className="stat-label">Salaries actifs</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{fmt(dashboard.monthly?.reduce((s, m) => s + parseFloat(m.total || 0), 0))}</div>
            <div className="stat-label">Masse salariale totale</div>
          </div>
        </div>
      )}

      {/* Filters — primary row */}
      <div className="pl-filters-bar">
        {!isOrgMode && (
          <div className="pl-filter-group">
            <label>Exercice</label>
            <select value={selectedExercice} onChange={e => setSelectedExercice(e.target.value)}>
              {exercices.map(ex => <option key={ex.id} value={ex.id}>{ex.annee}</option>)}
            </select>
          </div>
        )}
        <div className="pl-filter-group">
          <label>Societe</label>
          <select value={selectedSociete} onChange={e => setSelectedSociete(e.target.value)}>
            <option value="">Toutes</option>
            {groupes.length > 0 && (
              <optgroup label="Groupes">
                {groupes.map(g => <option key={`g${g.id}`} value={`g:${g.id}`}>{g.nom}</option>)}
              </optgroup>
            )}
            <optgroup label="Societes">
              {societes.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </optgroup>
          </select>
        </div>
        <div className="pl-filter-group">
          <label>Activite</label>
          <select value={selectedActivite} onChange={e => setSelectedActivite(e.target.value)}>
            <option value="global">Toutes</option>
            {activites.map(a => <option key={a.id} value={a.id}>{a.libelle}</option>)}
          </select>
        </div>
        <div className="pl-filter-group">
          <label>Recherche</label>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nom ou prenom..." />
        </div>
        {!isOrgMode && <div className="salaires-filter-toggles">
          <button className={`salaires-ecart-btn ${filterEcarts ? 'active' : ''}`} onClick={() => { setFilterEcarts(f => !f); setFilterSansActivite(false); }}>
            {filterEcarts ? 'Tous les salaries' : 'Ecarts uniquement'}
          </button>
          <button className={`salaires-ecart-btn sans-activite ${filterSansActivite ? 'active' : ''}`} onClick={() => { setFilterSansActivite(f => !f); setFilterEcarts(false); }}>
            {filterSansActivite ? 'Tous les salaries' : 'Sans activite'}
          </button>
        </div>}
      </div>

      {/* Actions bar — secondary */}
      {!isOrgMode && (
      <div className="salaires-actions-bar">
        <button className="salaires-export-btn" onClick={handleExport}>Export Excel</button>
        {isAdmin && (
          <>
            <label className="salaires-import-xl-btn" title="Fichier « Détails des agrégats » — alimente toutes les lignes de detail">
              {agregatsImporting ? 'Import en cours...' : 'Importer agregats Excel'}
              <input type="file" accept=".xlsx,.xls" onChange={handleAgregatsUpload} hidden disabled={agregatsImporting} />
            </label>
            <label className="salaires-import-pdf-btn" title="Alimente uniquement la ligne « Total verse BP »">
              {pdfImporting ? 'Lecture PDF...' : 'Importer bulletins PDF (Total verse BP)'}
              {!pdfImporting && <span className="btn-hint">plusieurs fichiers possibles</span>}
              <input type="file" accept=".pdf" multiple onChange={handlePdfUpload} hidden disabled={pdfImporting} />
            </label>
            <button className="salaires-add-btn" onClick={() => setShowAdd(true)}>+ Ajouter</button>
            <div className="salaires-more-actions">
              <button className="salaires-export-btn" onClick={() => setShowActionsMenu(v => !v)}>
                Plus d'actions ▾
              </button>
              {showActionsMenu && (
                <div className="actions-dropdown" onMouseLeave={() => setShowActionsMenu(false)}>
                  <button onClick={() => { setCoeffInput(coeffDefault); setShowCoeffModal(true); setShowActionsMenu(false); }}>
                    Coefficient ({coeffDefault})
                  </button>
                  <hr />
                  <div className="purge-mois-group">
                    <select value={purgeMois} onChange={e => { setPurgeMois(e.target.value); setPurgeConfirm(null); }} className="purge-mois-select">
                      <option value="">Purger un mois...</option>
                      {MOIS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                    {purgeMois && !purgeConfirm && <button className="salaires-purge-btn" onClick={handlePurgeMois}>Supprimer</button>}
                    {purgeConfirm && purgeConfirm.mois === purgeMois && (
                      <button className="salaires-purge-btn purge-confirm" onClick={handlePurgeMois}>
                        Confirmer suppression {purgeConfirm.label} ?
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      )}

      {/* Compte rendu de l'import des agregats */}
      {agregatsResult?.diagnostic && (
        <div className="import-result">
          <div className="import-result-summary">
            <span className="import-badge erreur">Import impossible</span>
            <button className="import-close" onClick={() => setAgregatsResult(null)}>x</button>
          </div>
          <div className="import-errors">
            <div className="import-error-line">
              Feuille lue : <b>{agregatsResult.diagnostic.feuille_lue}</b>
              {agregatsResult.diagnostic.feuilles.length > 1 && ` (sur ${agregatsResult.diagnostic.feuilles.join(', ')})`}
              {' · '}{agregatsResult.diagnostic.lignes_dans_le_fichier} ligne(s) dans le fichier
            </div>
            {agregatsResult.diagnostic.colonnes_manquantes?.length > 0 && (
              <div className="import-error-line">
                <b>Colonnes manquantes :</b> {agregatsResult.diagnostic.colonnes_manquantes.join(', ')}
              </div>
            )}
            <div className="import-error-line">
              Motifs de rejet : {Object.entries(agregatsResult.diagnostic.rejets)
                .filter(([, n]) => n > 0).map(([k, n]) => `${k.replace(/_/g, ' ')} : ${n}`).join(' · ') || 'aucun'}
            </div>
            {agregatsResult.diagnostic.annees_rencontrees?.length > 0 && (
              <div className="import-error-line">
                Annees du fichier : {agregatsResult.diagnostic.annees_rencontrees.join(', ')}
                {' · '}exercices en base : {agregatsResult.diagnostic.exercices_disponibles.join(', ')}
              </div>
            )}
            <div className="import-error-line" style={{ opacity: 0.75 }}>
              Colonnes trouvees : {agregatsResult.diagnostic.colonnes_trouvees.join(' | ')}
            </div>
          </div>
        </div>
      )}

      {agregatsResult && !agregatsResult.echec && (
        <div className="import-result">
          <div className="import-result-summary">
            <span className="import-badge created">{agregatsResult.lignes_inserees} ligne(s) importee(s)</span>
            <span className="import-badge details">{agregatsResult.lignes_supprimees} remplacee(s)</span>
            <span className="import-badge updated">{agregatsResult.mois.length} mois : {agregatsResult.mois.join(', ')}</span>
            {agregatsResult.salaries_crees?.length > 0 && (
              <span className="import-badge created">{agregatsResult.salaries_crees.length} salarie(s) cree(s)</span>
            )}
            {agregatsResult.rapproches_zeros_manquants > 0 && (
              <span className="import-badge erreur">{agregatsResult.rapproches_zeros_manquants} ligne(s) sans zeros de tete</span>
            )}
            <button className="import-close" onClick={() => setAgregatsResult(null)}>x</button>
          </div>
          {agregatsResult.salaries_crees?.length > 0 && (
            <div className="import-errors">
              <div className="import-error-line" style={{ fontWeight: 600 }}>
                Salaries absents de la base, crees a l'import :
              </div>
              {agregatsResult.salaries_crees.map(c => (
                <div className="import-error-line" key={c.numero}>
                  {c.numero} — {c.nom}{c.prenom ? ` ${c.prenom}` : ''}
                  {c.qualification ? ` · ${c.qualification}` : ''}
                  {c.date_fin_contrat
                    ? ` · sorti au ${c.date_fin_contrat.substring(0, 10).split('-').reverse().join('/')} (dernier bulletin ${c.dernier_mois})`
                    : ' · toujours present'}
                </div>
              ))}
            </div>
          )}
          {agregatsResult.rapproches_zeros_manquants > 0 && (
            <div className="import-errors">
              <div className="import-error-line">
                {agregatsResult.rapproches_zeros_manquants} ligne(s) avaient un matricule sans zeros de tete
                (colonne au format nombre au lieu de texte) — rattrapees automatiquement.
              </div>
            </div>
          )}
          {agregatsResult.annees_inconnues?.length > 0 && (
            <div className="import-errors">
              <div className="import-error-line">
                Exercices absents en base : {agregatsResult.annees_inconnues.join(', ')} — creez-les puis relancez l'import.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import result */}
      {importResult && (
        <div className="import-result">
          <div className="import-result-summary">
            {importResult.created > 0 && <span className="import-badge created">{importResult.created} cree(s)</span>}
            {importResult.updated > 0 && <span className="import-badge updated">{importResult.updated} mis a jour</span>}
            {importResult.detailsInserted > 0 && <span className="import-badge details">{importResult.detailsInserted} detail(s)</span>}
            <button className="import-close" onClick={() => setImportResult(null)}>x</button>
          </div>
          {importResult.errors && importResult.errors.length > 0 && (
            <div className="import-errors">
              {importResult.errors.map((err, i) => <div key={i} className="import-error-line">{err}</div>)}
            </div>
          )}
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <div className="salaires-add-form">
          <input value={newSalarie.nom} onChange={e => setNewSalarie(p => ({ ...p, nom: e.target.value }))} placeholder="Nom" />
          <input value={newSalarie.prenom} onChange={e => setNewSalarie(p => ({ ...p, prenom: e.target.value }))} placeholder="Prenom" />
          <select value={newSalarie.societe_id} onChange={e => setNewSalarie(p => ({ ...p, societe_id: e.target.value }))}>
            <option value="">Societe</option>
            {societes.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
          <select value={newSalarie.activite_id} onChange={e => setNewSalarie(p => ({ ...p, activite_id: e.target.value }))}>
            <option value="">Activite</option>
            {activites.map(a => <option key={a.id} value={a.id}>{a.libelle}</option>)}
          </select>
          <select value={newSalarie.categorie} onChange={e => setNewSalarie(p => ({ ...p, categorie: e.target.value }))}>
            <option value="productif">Productif</option>
            <option value="improductif">Improductif</option>
          </select>
          <button onClick={handleAddSalarie}>Valider</button>
          <button className="btn-cancel" onClick={() => setShowAdd(false)}>Annuler</button>
        </div>
      )}

      {/* Onglets — l'organigramme a sa propre entree dans le menu lateral */}
      {!isOrgMode && (
        <div className="salaires-tabs">
          <button className={activeView === 'liste' ? 'active' : ''} onClick={() => setActiveView('liste')}>Detail par salarie</button>
          <button className={activeView === 'recap' ? 'active' : ''} onClick={() => setActiveView('recap')}>Recap par activite</button>
          <button className={activeView === 'annuaire' ? 'active' : ''} onClick={() => setActiveView('annuaire')}>Liste des salaries</button>
          <button className={activeView === 'evolution' ? 'active' : ''} onClick={() => setActiveView('evolution')}>Evolution effectifs</button>
          {isAdmin && <button className={activeView === 'masse' ? 'active' : ''} onClick={() => setActiveView('masse')}>Masse salariale</button>}
          {isAdmin && <button className={activeView === 'licenciements' ? 'active' : ''} onClick={() => setActiveView('licenciements')}>Divers</button>}
        </div>
      )}

      {activeView === 'liste' && (loading ? (
        <div className="pl-loading">Chargement...</div>
      ) : (
        <div className="pl-table-container">
          <div className="pl-table-scroll">
            <table className="pl-salaries-table">
              <thead>
                <tr>
                  <th className="sortable-th" onClick={() => handleSortDetail('nom')}>Salarie{sortDetailIcon('nom')}</th>
                  <th>Type charge</th>
                  {MOIS.map((m, mi) => {
                    const mois = mi + 1;
                    const typeMois = getMoisTypeFn(mois);
                    return (
                      <th key={m} className={`sortable-th ${typeMois === 'budget' ? 'cell-budget' : ''}`}
                        onClick={() => handleSortDetail(mois)}
                        title="Trier sur le total de ce mois">
                        {m}{sortDetailIcon(mois)}
                        {isAdmin ? (
                          <span className={`mois-type-badge ${typeMois}`}
                            onClick={() => handleToggleMoisType(mois)}
                            title={typeMois === 'reel' ? 'Reel — cliquer pour passer en budget' : 'Budget — cliquer pour passer en reel'}>
                            {typeMois === 'reel' ? 'R' : 'B'}
                          </span>
                        ) : (
                          <span className={`mois-type-dot ${typeMois}`} title={typeMois === 'reel' ? 'Reel' : 'Budget'} />
                        )}
                      </th>
                    );
                  })}
                  <th className="sortable-th" onClick={() => handleSortDetail('total')}>Total{sortDetailIcon('total')}</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {salaries.length === 0 ? (
                  <tr><td colSpan={15} className="pl-empty-state">Aucun salarie</td></tr>
                ) : salaries.filter(s => {
                  if (filterSansActivite) return !s.activite_id;
                  if (!filterEcarts) return true;
                  // Garder uniquement les salariés avec au moins un écart
                  const sD = details.filter(d => d.salarie_id === s.id);
                  return MOIS.some((_, mi) => {
                    const mois = mi + 1;
                    const tv = totalVerse.find(t => t.salarie_id === s.id && t.mois === mois);
                    const tvVal = tv ? parseFloat(tv.montant) : 0;
                    if (tvVal === 0) return false;
                    const sum = sumDetailsForCalc(sD, mois);
                    return Math.abs(tvVal - sum) > 1;
                  });
                }).sort((a, b) => {
                  const va = valeurTriDetail(a, sortDetail.col);
                  const vb = valeurTriDetail(b, sortDetail.col);
                  if (va < vb) return sortDetail.dir === 'asc' ? -1 : 1;
                  if (va > vb) return sortDetail.dir === 'asc' ? 1 : -1;
                  return 0;
                }).map(s => {
                  const sDetails = details.filter(d => d.salarie_id === s.id);
                  const chargeTypeIds = [...new Set(sDetails.map(d => d.type_charge_id))];
                  const isAdding = addingChargeFor === s.id;
                  const existingTcIds = new Set(chargeTypeIds);
                  const availableTypes = typesCharges.filter(t => !existingTcIds.has(t.id));
                  const isSalarieExpanded = expandedSalaries[s.id];

                  // Ligne groupée (par défaut) : 1 ligne = nom + total versé par mois
                  if (!isSalarieExpanded) {
                    let annualGrouped = 0;
                    const moisCells = MOIS.map((_, mi) => {
                      const mois = mi + 1;
                      // Vue repliee = somme des lignes de detail issues de l'export de paie.
                      // Elle couvre tous les salaries et tous les mois, la ou le total du
                      // bulletin ne porte que sur les mois effectivement importes en PDF.
                      const val = sumDetailsForCalc(sDetails, mois);
                      annualGrouped += val;
                      const typeMois = getMoisTypeFn(mois);
                      return (
                        <td key={mi} className={typeMois === 'budget' ? 'cell-budget' : ''}>
                          {val > 0 ? fmt(Math.round(val)) : '-'}
                        </td>
                      );
                    });
                    return (
                      <tr key={s.id} className="salarie-grouped-row" onClick={() => {
                        setExpandedSalaries(prev => ({ ...prev, [s.id]: true }));
                        // Ouvrir toutes les sections de charges par défaut
                        setExpandedSections(prev => {
                          const next = { ...prev };
                          ['brut', 'cotisations', 'non_soumis'].forEach(sec => { next[`${s.id}-${sec}`] = true; });
                          return next;
                        });
                      }}>
                        <td className="salarie-name-cell salarie-grouped-name">
                          <span className="section-toggle">▶</span>
                          <span className="salarie-name">{s.nom} {s.prenom}</span>
                          {s.numero_salarie && <span className="salarie-numero">{s.numero_salarie}</span>}
                        </td>
                        <td className="salarie-grouped-type">Total verse</td>
                        {moisCells}
                        <td style={{ fontWeight: 700 }}>{fmt(Math.round(annualGrouped))}</td>
                        {isAdmin && <td></td>}
                      </tr>
                    );
                  }

                  // Grouper les types de charge par section
                  const SECTIONS = [
                    { key: 'brut', label: 'Salaire brut', expandable: true },
                    { key: 'cotisations', label: 'Cotisations sociales', expandable: true },
                    { key: 'non_soumis', label: 'Autres elements non soumis', expandable: true },
                  ];
                  const sectionGroups = {};
                  SECTIONS.forEach(sec => { sectionGroups[sec.key] = []; });
                  chargeTypeIds.forEach(tcId => {
                    const tc = typesCharges.find(t => t.id === tcId);
                    const sec = tc?.section || 'brut';
                    if (sectionGroups[sec]) sectionGroups[sec].push(tcId);
                    else sectionGroups['brut'].push(tcId);
                  });

                  // Compter les lignes visibles pour le rowSpan
                  const countVisibleRows = () => {
                    let count = 0;
                    SECTIONS.forEach(sec => {
                      const ids = sectionGroups[sec.key];
                      if (ids.length === 0) return;
                      count++; // en-tête section
                      if (sec.expandable && expandedSections[`${s.id}-${sec.key}`]) {
                        count += ids.length; // lignes détail
                      }
                    });
                    if (count === 0) count = 1; // "Aucune ligne"
                    count += 2; // Autres + Total versé
                    const addRows = isAdding ? 1 : (isAdmin && availableTypes.length > 0 ? 1 : 0);
                    count += addRows;
                    return count;
                  };
                  const totalRows = countVisibleRows();

                  // Meme base que la ligne repliee : la somme des lignes de detail.
                  // Afficher un total annuel calcule autrement que la colonne du dessus
                  // rendrait les deux chiffres incomparables.
                  let annualTotal = 0;
                  MOIS.forEach((_, mi) => { annualTotal += sumDetailsForCalc(sDetails, mi + 1); });

                  const result = [];
                  let isFirst = true;

                  // Cellule nom (commune a toutes les lignes)
                  const nameCell = (rowSpan) => (
                    <td rowSpan={rowSpan} className="salarie-name-cell">
                      <span className="section-toggle salarie-collapse-btn" onClick={() => setExpandedSalaries(prev => ({ ...prev, [s.id]: false }))}>▼</span>
                      {editingName === s.id ? (
                        <div className="salarie-name-edit">
                          <input value={editNameVal.nom} onChange={e => setEditNameVal(p => ({ ...p, nom: e.target.value }))} placeholder="Nom" autoFocus
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveName(s.id); if (e.key === 'Escape') setEditingName(null); }} />
                          <input value={editNameVal.prenom} onChange={e => setEditNameVal(p => ({ ...p, prenom: e.target.value }))} placeholder="Prenom"
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveName(s.id); if (e.key === 'Escape') setEditingName(null); }} />
                          <button className="add-charge-confirm" onClick={() => handleSaveName(s.id)}>OK</button>
                          <button className="add-charge-cancel" onClick={() => setEditingName(null)}>x</button>
                        </div>
                      ) : (
                        <div className={`salarie-name ${isAdmin ? 'pl-editable' : ''}`}
                          onClick={() => { if (!isAdmin) return; setEditingName(s.id); setEditNameVal({ nom: s.nom || '', prenom: s.prenom || '' }); }}>
                          {s.nom} {s.prenom}
                        </div>
                      )}
                      <div className="salarie-meta">
                        {s.numero_salarie && <span className="salarie-numero">{s.numero_salarie}</span>}
                        {isAdmin ? (
                          <select className="categorie-select" value={s.activite_id || ''} onChange={async e => {
                            const val = e.target.value || null;
                            await salairesApi.updateSalarie(s.id, { activite_id: val });
                            showToast('Activite modifiee');
                            const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
                            const sal = await salairesApi.getSalaries(params);
                            setSalaries(sal.data);
                          }}>
                            <option value="">Sans activite</option>
                            {activites.map(a => <option key={a.id} value={a.id}>{a.libelle}</option>)}
                          </select>
                        ) : (
                          s.activite_libelle && <span>{s.activite_libelle}</span>
                        )}
                        {s.activite_manuelle && (
                          <span className="activite-verrou" title="Activite saisie a la main : l'import de paie ne la modifie plus">
                            🔒 manuelle
                            {isAdmin && <button type="button" onClick={async () => {
                              await salairesApi.updateSalarie(s.id, { activite_manuelle: false });
                              showToast('Activite rendue a l\'import : reprise au prochain import de paie');
                              const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
                              const sal = await salairesApi.getSalaries(params);
                              setSalaries(sal.data);
                            }}>rendre a l'import</button>}
                          </span>
                        )}
                        {isAdmin ? (
                          <select className="categorie-select" value={s.poste_id || ''} onChange={async e => {
                            await salairesApi.updateSalarie(s.id, { poste_id: e.target.value || null });
                            showToast('Poste modifie');
                            const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
                            const sal = await salairesApi.getSalaries(params);
                            setSalaries(sal.data);
                          }}>
                            <option value="">Sans poste</option>
                            {postes.filter(p => !s.activite_id || p.activite_id === s.activite_id).map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                          </select>
                        ) : (
                          s.poste_libelle && <span>{s.poste_libelle}</span>
                        )}
                        {isAdmin ? (
                          <select className="categorie-select" value={s.categorie || 'productif'} onChange={e => { salairesApi.updateSalarie(s.id, { categorie: e.target.value }); s.categorie = e.target.value; showToast('Categorie modifiee'); }}>
                            <option value="productif">Productif</option>
                            <option value="improductif">Improductif</option>
                          </select>
                        ) : (
                          <span className={`categorie-badge ${s.categorie || 'productif'}`}>{s.categorie === 'improductif' ? 'Improductif' : 'Productif'}</span>
                        )}
                      </div>
                    </td>
                  );

                  const hasSections = SECTIONS.some(sec => sectionGroups[sec.key].length > 0);

                  if (!hasSections) {
                    result.push(
                      <tr key={`${s.id}-empty`}>
                        {nameCell(totalRows)}
                        <td colSpan={13} style={{ color: '#9ca3af', fontStyle: 'italic' }}>Aucune ligne de charge</td>
                        <td rowSpan={totalRows} style={{ fontWeight: 700 }}>{fmt(annualTotal)}</td>
                        {isAdmin && <td rowSpan={totalRows}><button className="pl-config-delete" onClick={() => handleDeleteSalarie(s.id)}>x</button></td>}
                      </tr>
                    );
                  } else {
                    SECTIONS.forEach(sec => {
                      const ids = sectionGroups[sec.key];
                      if (ids.length === 0) return;

                      const isExpanded = expandedSections[`${s.id}-${sec.key}`];
                      const canExpand = sec.expandable && ids.length > 0;

                      // En-tête section (toujours visible) avec sous-total
                      result.push(
                        <tr key={`${s.id}-sec-${sec.key}`}
                            className={`section-header section-${sec.key} ${canExpand ? 'section-clickable' : ''}`}
                            onClick={() => canExpand && toggleSection(s.id, sec.key)}>
                          {isFirst && nameCell(totalRows)}
                          <td className="section-label">
                            {canExpand && <span className="section-toggle">{isExpanded ? '▼' : '▶'}</span>}
                            {sec.label}
                          </td>
                          {MOIS.map((_, mi) => {
                            const mois = mi + 1;
                            const sum = ids.reduce((acc, tcId) => {
                              const det = sDetails.find(d => d.type_charge_id === tcId && d.mois === mois);
                              return acc + (det ? parseFloat(det.montant) || 0 : 0);
                            }, 0);
                            return <td key={mi} className="section-subtotal">{sum > 0 ? fmt(sum) : '-'}</td>;
                          })}
                          {isFirst && <td rowSpan={totalRows} style={{ fontWeight: 700 }}>{fmt(annualTotal)}</td>}
                          {isFirst && isAdmin && <td rowSpan={totalRows}><button className="pl-config-delete" onClick={() => handleDeleteSalarie(s.id)}>x</button></td>}
                        </tr>
                      );
                      isFirst = false;

                      // Lignes de détail (seulement si expanded)
                      if (canExpand && isExpanded) {
                        ids.forEach(tcId => {
                          const tc = typesCharges.find(t => t.id === tcId);
                          result.push(
                            <tr key={`${s.id}-${tcId}`} className={`section-detail-row ${tc?.exclure_calcul ? 'row-excluded' : ''}`}>
                              <td className="section-detail-label">
                                {isAdmin && <input type="checkbox" className="exclude-checkbox"
                                  checked={!!tc?.exclure_calcul}
                                  title={tc?.exclure_calcul ? 'Inclus dans le calcul si décoché' : 'Exclure du calcul total'}
                                  onChange={async (e) => {
                                    e.stopPropagation();
                                    await salairesApi.updateTypeCharge(tcId, { exclure_calcul: e.target.checked });
                                    const res = await salairesApi.getTypesCharges();
                                    setTypesCharges(res.data);
                                  }} />}
                                {tc ? tc.libelle : '-'}
                              </td>
                              {MOIS.map((_, mi) => {
                                const mois = mi + 1;
                                const det = sDetails.find(d => d.type_charge_id === tcId && d.mois === mois);
                                const cellKey = `${s.id}-${tcId}-${mois}`;
                                return (
                                  <td key={mi}>
                                    {editingCell === cellKey ? (
                                      <input className="pl-inline-edit" type="number" value={editingValue}
                                        onChange={e => setEditingValue(e.target.value)}
                                        onBlur={() => setEditingCell(null)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') handleSaveDetail(s.id, mois, tcId);
                                          if (e.key === 'Escape') setEditingCell(null);
                                        }}
                                        autoFocus />
                                    ) : (
                                      <span className={isAdmin ? 'pl-editable' : ''}
                                        onClick={() => { if (!isAdmin) return; setEditingCell(cellKey); setEditingValue(det?.montant || ''); }}>
                                        {det ? fmt(det.montant) : '-'}
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        });
                      }
                    });
                  }

                  // Ligne « Total verse XL » — somme des agregats importes depuis l'Excel
                  result.push(
                    <tr key={`${s.id}-total-xl`} className="row-total-xl">
                      <td style={{ fontWeight: 600 }}>Total verse XL</td>
                      {MOIS.map((_, mi) => {
                        const mois = mi + 1;
                        const sommeLignes = sumDetailsForCalc(sDetails, mois);
                        return <td key={mi}>{sommeLignes !== 0 ? fmt(Math.round(sommeLignes)) : '-'}</td>;
                      })}
                    </tr>
                  );

                  // Ligne "Total verse"
                  result.push(
                    <tr key={`${s.id}-total-verse`} className="row-total-verse">
                      <td style={{ fontWeight: 600 }}>
                        Total verse BP
                        {isAdmin && (
                          projeterFor === s.id ? (
                            <span className="projeter-inline" onClick={e => e.stopPropagation()}>
                              <select value={projeterMoisSource} onChange={e => setProjecterMoisSource(e.target.value)}>
                                <option value="">Mois source...</option>
                                {MOIS.map((m, mi) => {
                                  const mois = mi + 1;
                                  if (getMoisTypeFn(mois) !== 'reel') return null;
                                  return <option key={mois} value={mois}>{m}</option>;
                                })}
                              </select>
                              <button className="add-charge-confirm" onClick={() => handleExtendPrevisions(s.id)}>OK</button>
                              <button className="add-charge-cancel" onClick={() => { setProjecterFor(null); setProjecterMoisSource(''); }}>x</button>
                            </span>
                          ) : (
                            <button className="extend-btn" onClick={e => { e.stopPropagation(); setProjecterFor(s.id); setProjecterMoisSource(''); }}
                              title="Projeter les donnees d'un mois reel vers les mois budget suivants">Projeter</button>
                          )
                        )}
                      </td>
                      {MOIS.map((_, mi) => {
                        const mois = mi + 1;
                        const tv = totalVerse.find(t => t.salarie_id === s.id && t.mois === mois);
                        const tvVal = tv ? parseFloat(tv.montant) : 0;
                        const cellKey = `tv-${s.id}-${mois}`;
                        const typeMois = getMoisTypeFn(mois);

                        if (editingCell === cellKey) {
                          return (
                            <td key={mi} className={typeMois === 'budget' ? 'cell-budget' : ''}>
                              <input className="pl-inline-edit" type="number" value={editingValue}
                                onChange={e => setEditingValue(e.target.value)}
                                onBlur={() => setEditingCell(null)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleSaveTotalVerse(s.id, mois);
                                  if (e.key === 'Escape') setEditingCell(null);
                                }}
                                autoFocus />
                            </td>
                          );
                        }
                        return (
                          <td key={mi} className={`${tvVal > 0 ? '' : 'estimation-cell'} ${typeMois === 'budget' ? 'cell-budget' : ''}`}>
                            <span className={isAdmin ? 'pl-editable' : ''}
                              onClick={() => { if (!isAdmin) return; setEditingCell(cellKey); setEditingValue(tvVal || ''); }}>
                              {tvVal > 0 ? fmt(tvVal) : '-'}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );

                  // Ligne « Ecart BP - XL ». Les deux totaux mesurent la meme
                  // grandeur (cout employeur) par deux chemins : le bulletin PDF
                  // d'un cote, la somme des agregats de l'export de l'autre. Un
                  // ecart signale donc un desaccord entre les deux sources.
                  // Affichage sobre, sans pastille : un montant, ou rien.
                  result.push(
                    <tr key={`${s.id}-ecart`} className="row-ecart">
                      <td style={{ fontWeight: 600 }}>Ecart BP - XL</td>
                      {MOIS.map((_, mi) => {
                        const mois = mi + 1;
                        const tv = totalVerse.find(t => t.salarie_id === s.id && t.mois === mois);
                        const tvVal = tv ? parseFloat(tv.montant) : 0;
                        const sommeLignes = sumDetailsForCalc(sDetails, mois);
                        // Sans les deux termes, l'ecart n'a pas de sens
                        if (tvVal === 0 || sommeLignes === 0) return <td key={mi} className="ecart-neutre">-</td>;
                        const e = Math.round(tvVal - sommeLignes);
                        if (Math.abs(e) <= 1) return <td key={mi} className="ecart-neutre">0</td>;
                        return <td key={mi} className="ecart-present">{e > 0 ? `+${fmt(e)}` : fmt(e)}</td>;
                      })}
                    </tr>
                  );

                  // Ligne d'ajout inline
                  if (isAdding) {
                    result.push(
                      <tr key={`${s.id}-add-line`} className="add-charge-row">
                        <td colSpan={13}>
                          <div className="add-charge-inline">
                            <select value={newChargeTypeId} onChange={e => setNewChargeTypeId(e.target.value)} autoFocus>
                              <option value="">Choisir un type de charge...</option>
                              {availableTypes.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
                            </select>
                            <button className="add-charge-confirm" onClick={() => handleAddChargeLine(s.id)}>Ajouter</button>
                            <button className="add-charge-cancel" onClick={() => { setAddingChargeFor(null); setNewChargeTypeId(''); }}>Annuler</button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  // Bouton "+ Ajouter une ligne"
                  if (isAdmin && availableTypes.length > 0 && !isAdding) {
                    result.push(
                      <tr key={`${s.id}-add-btn`}>
                        <td colSpan={13}>
                          <button className="add-charge-btn" onClick={() => { setAddingChargeFor(s.id); setNewChargeTypeId(''); }}>+ Ajouter une ligne</button>
                        </td>
                      </tr>
                    );
                  }

                  return result;
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Onglet Recap par activité */}
      {activeView === 'recap' && (
        <div className="pl-table-container">
          <div className="pl-table-scroll">
            <table className="pl-salaries-table recap-table">
              <thead>
                <tr>
                  <th>Activite</th>
                  {MOIS.map(m => <th key={m}>{m}</th>)}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Calcul du total mensuel d'un salarié
                  const getSalarieMonths = (sid) => MOIS.map((_, mi) => {
                    const mois = mi + 1;
                    const tv = totalVerse.find(t => t.salarie_id === sid && t.mois === mois);
                    if (tv && parseFloat(tv.montant) > 0) return parseFloat(tv.montant);
                    const sD = details.filter(d => d.salarie_id === sid);
                    const sum = sumDetailsForCalc(sD, mois);
                    const ratio = getAutresRatio(sid);
                    return sum > 0 ? sum * ratio : 0;
                  });

                  const sumMonths = (arr) => arr.reduce((a, b) => a.map((v, i) => v + b[i]), Array(12).fill(0));

                  // Grouper : activité > poste > collaborateurs
                  const activiteMap = {};
                  salaries.forEach(s => {
                    const actKey = s.activite_id || 'sans';
                    const actLabel = s.activite_libelle || 'Sans activite';
                    if (!activiteMap[actKey]) activiteMap[actKey] = { label: actLabel, posteMap: {} };

                    const posteKey = s.poste_id || 'sans';
                    const posteLabel = s.poste_libelle || 'Sans poste';
                    if (!activiteMap[actKey].posteMap[posteKey]) activiteMap[actKey].posteMap[posteKey] = { label: posteLabel, salaries: [] };
                    activiteMap[actKey].posteMap[posteKey].salaries.push(s);
                  });

                  const rows = [];
                  const grandTotal = Array(12).fill(0);
                  const toggleRecap = (key) => setRecapExpanded(prev => ({ ...prev, [key]: !prev[key] }));

                  Object.entries(activiteMap).sort((a, b) => a[1].label.localeCompare(b[1].label)).forEach(([actKey, { label: actLabel, posteMap }]) => {
                    // Calcul total activité
                    const allSids = Object.values(posteMap).flatMap(p => p.salaries.map(s => s.id));
                    const actMonths = sumMonths(allSids.map(getSalarieMonths));
                    const actYear = actMonths.reduce((a, b) => a + b, 0);
                    actMonths.forEach((v, i) => { grandTotal[i] += v; });

                    const actExpanded = recapExpanded[`act-${actKey}`];

                    // Ligne activité
                    rows.push(
                      <tr key={`act-${actKey}`} className="recap-activite-row" onClick={() => toggleRecap(`act-${actKey}`)}>
                        <td><span className="recap-toggle">{actExpanded ? '▼' : '▶'}</span> {actLabel}</td>
                        {actMonths.map((v, i) => <td key={i}>{v > 0 ? fmt(Math.round(v)) : '-'}</td>)}
                        <td>{fmt(Math.round(actYear))}</td>
                      </tr>
                    );

                    // Sous-détail postes
                    if (actExpanded) {
                      Object.entries(posteMap).sort((a, b) => a[1].label.localeCompare(b[1].label)).forEach(([posteKey, { label: posteLabel, salaries: posteSalaries }]) => {
                        const posteSids = posteSalaries.map(s => s.id);
                        const posteMonths = sumMonths(posteSids.map(getSalarieMonths));
                        const posteYear = posteMonths.reduce((a, b) => a + b, 0);

                        const posteExpanded = recapExpanded[`poste-${actKey}-${posteKey}`];

                        rows.push(
                          <tr key={`poste-${actKey}-${posteKey}`} className="recap-poste-row" onClick={() => toggleRecap(`poste-${actKey}-${posteKey}`)}>
                            <td><span className="recap-toggle">{posteExpanded ? '▼' : '▶'}</span> {posteLabel}</td>
                            {posteMonths.map((v, i) => <td key={i}>{v > 0 ? fmt(Math.round(v)) : '-'}</td>)}
                            <td>{fmt(Math.round(posteYear))}</td>
                          </tr>
                        );

                        // Détail collaborateurs
                        if (posteExpanded) {
                          posteSalaries.sort((a, b) => (a.nom || '').localeCompare(b.nom || '')).forEach(s => {
                            const sMonths = getSalarieMonths(s.id);
                            const sYear = sMonths.reduce((a, b) => a + b, 0);
                            rows.push(
                              <tr key={`sal-${s.id}`} className="recap-salarie-row">
                                <td>{s.nom} {s.prenom}</td>
                                {sMonths.map((v, i) => <td key={i}>{v > 0 ? fmt(Math.round(v)) : '-'}</td>)}
                                <td>{fmt(Math.round(sYear))}</td>
                              </tr>
                            );
                          });
                        }
                      });
                    }
                  });

                  const grandYearTotal = grandTotal.reduce((a, b) => a + b, 0);
                  rows.push(
                    <tr key="total" className="recap-total-row">
                      <td>TOTAL</td>
                      {grandTotal.map((v, i) => <td key={i}>{v > 0 ? fmt(Math.round(v)) : '-'}</td>)}
                      <td>{fmt(Math.round(grandYearTotal))}</td>
                    </tr>
                  );

                  return rows;
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeView === 'annuaire' && (loading ? (
        <div className="pl-loading">Chargement...</div>
      ) : (
        <div>
        {isAdmin && (
          <div className="annuaire-toolbar">
            <label className="salaires-import-pdf-btn">
              {annuairePdfImporting ? 'Lecture PDF...' : 'Importer fiches PDF'}
              <input type="file" accept=".pdf" multiple onChange={handleAnnuairePdfUpload} hidden disabled={annuairePdfImporting} />
            </label>
          </div>
        )}
        <div className="pl-table-container">
          <div className="pl-table-scroll">
            <table className="pl-salaries-table annuaire-table">
              <thead>
                <tr className="annuaire-bandeau">
                  <th colSpan={7} className="bandeau-importe">
                    Importe de la paie — rafraichi a chaque import, non modifiable
                  </th>
                  <th colSpan={8} className="bandeau-interne">Saisie interne</th>
                  <th></th>
                </tr>
                <tr>
                  {[
                    { key: 'numero_salarie', label: 'Matricule', g: 'importe' },
                    { key: 'nom', label: 'Nom', g: 'importe' },
                    { key: 'prenom', label: 'Prenom', g: 'importe' },
                    { key: 'date_anciennete', label: "Anciennete", g: 'importe' },
                    { key: 'qualification', label: 'Qualification', g: 'importe' },
                    { key: 'activite_libelle', label: 'Activite', g: 'importe' },
                    { key: 'service_paie', label: 'Service', g: 'importe' },
                    { key: 'societe_nom', label: 'Societe', g: 'interne' },
                    { key: 'poste_libelle', label: 'Poste', g: 'interne' },
                    { key: 'service_libelle', label: 'Service interne', g: 'interne' },
                    { key: 'superieur_nom', label: 'Superieur', g: 'interne' },
                    { key: 'type_contrat', label: 'Type contrat', g: 'interne' },
                    { key: 'qualification_interne', label: 'Qualification interne', g: 'interne' },
                    { key: 'date_fin_contrat', label: 'Fin contrat', g: 'interne' },
                    { key: 'motif_fin_contrat', label: 'Motif', g: 'interne' },
                  ].map(c => (
                    <th key={c.key} className={`sortable-th col-${c.g}`} onClick={() => handleSortAnnuaire(c.key)}>
                      {c.label}{sortAnnuaireIcon(c.key)}
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {salaries.filter(s => {
                  if (search) {
                    const q = search.toLowerCase();
                    return (s.nom || '').toLowerCase().includes(q) || (s.prenom || '').toLowerCase().includes(q);
                  }
                  return true;
                }).sort((a, b) => {
                  const col = sortAnnuaire.col;
                  let va = a[col], vb = b[col];
                  if (va == null) va = '';
                  if (vb == null) vb = '';
                  if (col === 'date_anciennete' || col === 'date_fin_contrat') {
                    va = va ? new Date(va).getTime() : 0;
                    vb = vb ? new Date(vb).getTime() : 0;
                  } else {
                    va = String(va).toLowerCase();
                    vb = String(vb).toLowerCase();
                  }
                  if (va < vb) return sortAnnuaire.dir === 'asc' ? -1 : 1;
                  if (va > vb) return sortAnnuaire.dir === 'asc' ? 1 : -1;
                  return 0;
                }).map(s => editingAnnuaire === s.id ? (
                  <tr key={s.id} className="annuaire-editing-row">
                    <td className="cell-importe">{s.numero_salarie || '-'}</td>
                    <td className="cell-importe">{s.nom || '-'}</td>
                    <td className="cell-importe">{s.prenom || '-'}</td>
                    <td className="cell-importe">{s.date_anciennete ? new Date(s.date_anciennete).toLocaleDateString('fr-FR') : '-'}</td>
                    <td className="cell-importe">{s.qualification || '-'}</td>
                    <td className="cell-importe">{s.activite_libelle || '-'}</td>
                    <td className="cell-importe">{s.service_paie || '-'}</td>
                    <td>
                      <select value={editAnnuaireVal.societe_id} onChange={e => setEditAnnuaireVal(v => ({ ...v, societe_id: e.target.value }))}>
                        <option value="">--</option>
                        {societes.filter(x => x.actif).map(x => <option key={x.id} value={x.id}>{x.nom}</option>)}
                      </select>
                    </td>
                    <td>
                      <select value={editAnnuaireVal.poste_id} onChange={e => setEditAnnuaireVal(v => ({ ...v, poste_id: e.target.value }))}>
                        <option value="">--</option>
                        {postes.filter(p => p.actif).map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                      </select>
                    </td>
                    <td>
                      <select value={editAnnuaireVal.service_id || ''} onChange={e => setEditAnnuaireVal(v => ({ ...v, service_id: e.target.value }))}>
                        <option value="">--</option>
                        {services.map(sv => <option key={sv.id} value={sv.id}>{sv.libelle}</option>)}
                      </select>
                    </td>
                    <td>
                      <select value={editAnnuaireVal.superieur_id} onChange={e => setEditAnnuaireVal(v => ({ ...v, superieur_id: e.target.value }))}>
                        <option value="">--</option>
                        {allSalaries.filter(x => x.id !== s.id).map(x => <option key={x.id} value={x.id}>{x.nom} {x.prenom || ''}</option>)}
                      </select>
                    </td>
                    <td>
                      <select value={editAnnuaireVal.type_contrat || ''} onChange={e => setEditAnnuaireVal(v => ({ ...v, type_contrat: e.target.value }))}>
                        <option value="">--</option>
                        {TYPES_CONTRAT.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td><input value={editAnnuaireVal.qualification_interne || ''} onChange={e => setEditAnnuaireVal(v => ({ ...v, qualification_interne: e.target.value }))} placeholder="Libelle interne" /></td>
                    <td><input type="date" value={editAnnuaireVal.date_fin_contrat} onChange={e => setEditAnnuaireVal(v => ({ ...v, date_fin_contrat: e.target.value }))} /></td>
                    <td><input value={editAnnuaireVal.motif_fin_contrat} onChange={e => setEditAnnuaireVal(v => ({ ...v, motif_fin_contrat: e.target.value }))} placeholder="Ex: Demission..." /></td>
                    <td className="annuaire-actions">
                      <button className="annuaire-btn annuaire-btn-save" onClick={handleSaveAnnuaire} title="Enregistrer">✓</button>
                      <button className="annuaire-btn annuaire-btn-cancel" onClick={() => setEditingAnnuaire(null)} title="Annuler">✕</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={s.id} className={s.motif_fin_contrat ? 'annuaire-row-inactif' : ''}>
                    <td className="cell-importe">{s.numero_salarie || '-'}</td>
                    <td className="cell-importe">{s.nom || '-'}</td>
                    <td className="cell-importe">{s.prenom || '-'}</td>
                    <td className="cell-importe">{s.date_anciennete ? new Date(s.date_anciennete).toLocaleDateString('fr-FR') : '-'}</td>
                    <td className="cell-importe">{s.qualification || '-'}</td>
                    <td className="cell-importe">{s.activite_libelle || '-'}{s.activite_manuelle && <span title="Saisie a la main, non ecrasee par l'import"> 🔒</span>}</td>
                    <td className="cell-importe">{s.service_paie || '-'}</td>
                    <td>{s.societe_nom || '-'}</td>
                    <td>{isAdmin ? (
                      <select className="categorie-select" value={s.poste_id || ''} onChange={async e => {
                        await salairesApi.updateSalarie(s.id, { poste_id: e.target.value || null });
                        showToast('Poste modifie');
                        const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
                        const sal = await salairesApi.getSalaries(params);
                        setSalaries(sal.data);
                      }}>
                        <option value="">Sans poste</option>
                        {postes.filter(p => p.actif).map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                      </select>
                    ) : (s.poste_libelle || '-')}</td>
                    <td>{isAdmin ? (
                      <select className="categorie-select" value={s.service_id || ''} onChange={async e => {
                        await salairesApi.updateSalarie(s.id, { service_id: e.target.value || null });
                        showToast('Service interne modifie');
                        const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
                        const sal = await salairesApi.getSalaries(params);
                        setSalaries(sal.data);
                      }}>
                        <option value="">--</option>
                        {services.map(sv => <option key={sv.id} value={sv.id}>{sv.libelle}</option>)}
                      </select>
                    ) : (s.service_libelle || '-')}</td>
                    <td>{isAdmin ? (
                      <select className="categorie-select" value={s.superieur_id || ''} onChange={async e => {
                        await salairesApi.updateSalarie(s.id, { superieur_id: e.target.value || null });
                        showToast('Superieur modifie');
                        const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
                        const sal = await salairesApi.getSalaries(params);
                        setSalaries(sal.data);
                      }}>
                        <option value="">--</option>
                        {allSalaries.filter(x => x.id !== s.id).map(x => <option key={x.id} value={x.id}>{x.nom} {x.prenom || ''}</option>)}
                      </select>
                    ) : (s.superieur_nom ? `${s.superieur_nom} ${s.superieur_prenom || ''}`.trim() : '-')}</td>
                    <td>{isAdmin ? (
                      <select className="categorie-select" value={s.type_contrat || ''} onChange={async e => {
                        await salairesApi.updateSalarie(s.id, { type_contrat: e.target.value || null });
                        showToast('Type de contrat modifie');
                        const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
                        const sal = await salairesApi.getSalaries(params);
                        setSalaries(sal.data);
                      }}>
                        <option value="">--</option>
                        {TYPES_CONTRAT.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    ) : (s.type_contrat || '-')}</td>
                    <td>{isAdmin ? (
                      <input className="annuaire-input-inline" defaultValue={s.qualification_interne || ''}
                        placeholder="—" title="Libelle interne, libre"
                        onBlur={async e => {
                          const v = e.target.value.trim();
                          if (v === (s.qualification_interne || '')) return;
                          await salairesApi.updateSalarie(s.id, { qualification_interne: v || null });
                          showToast('Qualification interne modifiee');
                        const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
                        const sal = await salairesApi.getSalaries(params);
                        setSalaries(sal.data);
                        }} />
                    ) : (s.qualification_interne || '-')}</td>
                    <td>{s.date_fin_contrat ? new Date(s.date_fin_contrat).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>{s.motif_fin_contrat || '-'}</td>
                    <td className="annuaire-actions">
                      <button className="annuaire-btn annuaire-btn-contrat" onClick={() => openFicheContrat(s.id)} title="Fiche contrat">&#9776;</button>
                      {isAdmin && <button className="annuaire-btn annuaire-btn-edit" onClick={() => handleStartEditAnnuaire(s)} title="Modifier">&#9998;</button>}
                    </td>
                  </tr>
                ))}
                {salaries.length === 0 && (
                  <tr><td colSpan={13} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Aucun salarie</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modale preview import annuaire */}
        {annuairePdfPreview && (
          <div className="pdf-modal-overlay" onClick={() => setAnnuairePdfPreview(null)}>
            <div className="annuaire-import-modal" onClick={e => e.stopPropagation()}>
              <h3>Import fiches salaries — Apercu</h3>
              <p style={{ fontSize: '0.825rem', color: '#6b7280', margin: '4px 0 12px' }}>
                {annuairePdfPreview.length} bulletin(s) detecte(s) — les champs detectes seront mis a jour.
              </p>
              <div className="annuaire-import-scroll">
                <table className="annuaire-import-table">
                  <thead>
                    <tr>
                      <th>Matricule</th>
                      <th>Nom Prenom</th>
                      <th>Societe (detectee)</th>
                      <th>Qualification (detectee)</th>
                      <th>Dt anciennete (detectee)</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {annuairePdfPreview.map(b => (
                      <tr key={b.numero_salarie} className={b.will_update ? '' : 'annuaire-import-unknown'}>
                        <td>{b.numero_salarie}</td>
                        <td>{b.existing_nom || `${b.nom || ''} ${b.prenom || ''}`}</td>
                        <td>
                          {b.societe || '-'}
                          {b.societe_matched && <span className="annuaire-import-match"> → {b.societe_matched}</span>}
                        </td>
                        <td>
                          {b.qualification || '-'}
                          {b.existing_qualification && b.qualification && b.existing_qualification !== b.qualification && (
                            <span className="annuaire-import-old"> (ancien: {b.existing_qualification})</span>
                          )}
                        </td>
                        <td>
                          {b.date_anciennete ? new Date(b.date_anciennete).toLocaleDateString('fr-FR') : '-'}
                          {b.existing_date_anciennete && b.date_anciennete && b.existing_date_anciennete.substring(0, 10) !== b.date_anciennete && (
                            <span className="annuaire-import-old"> (ancien: {new Date(b.existing_date_anciennete).toLocaleDateString('fr-FR')})</span>
                          )}
                        </td>
                        <td>
                          {b.will_update
                            ? <span className="annuaire-import-badge ok">Mise a jour</span>
                            : <span className="annuaire-import-badge skip">Inconnu (ignore)</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="annuaire-import-footer">
                <button className="pdf-confirm-btn" onClick={handleAnnuairePdfConfirm} disabled={annuairePdfImporting}>
                  {annuairePdfImporting ? 'Import en cours...' : `Confirmer (${annuairePdfPreview.filter(b => b.will_update).length} salarie(s))`}
                </button>
                <button className="btn-cancel" onClick={() => setAnnuairePdfPreview(null)}>Annuler</button>
              </div>
            </div>
          </div>
        )}
        </div>
      ))}

      {/* Onglet Organigramme */}
      {activeView === 'organigramme' && (() => {
        const buildOrgTree = (list) => {
          const map = {};
          const roots = [];
          list.forEach(s => { map[s.id] = { ...s, children: [] }; });
          list.forEach(s => {
            if (s.superieur_id && map[s.superieur_id]) map[s.superieur_id].children.push(map[s.id]);
            else roots.push(map[s.id]);
          });
          // Trier les enfants par ordre_org
          Object.values(map).forEach(n => n.children.sort((a, b) => (a.ordre_org || 0) - (b.ordre_org || 0)));
          return roots.sort((a, b) => (a.ordre_org || 0) - (b.ordre_org || 0));
        };
        const today = new Date().toISOString().substring(0, 10);
        const activeSalaries = salaries.filter(s => !s.date_fin_contrat || s.date_fin_contrat.substring(0, 10) >= today);
        // Les salaries masques sortent de l'arbre sans disparaitre de l'application :
        // leurs N-1 sont rattaches visuellement au premier N+1 encore visible.
        const masques = activeSalaries.filter(s => s.exclure_organigramme);
        const masquesIds = new Set(masques.map(s => s.id));
        const superieurParId = {};
        activeSalaries.forEach(s => { superieurParId[s.id] = s.superieur_id || null; });
        const premierSuperieurVisible = (s) => {
          let p = s.superieur_id || null;
          const vus = new Set();
          while (p && masquesIds.has(p) && !vus.has(p)) { vus.add(p); p = superieurParId[p] || null; }
          return p && !masquesIds.has(p) ? p : null;
        };
        const orgSource = activeSalaries
          .filter(s => !masquesIds.has(s.id))
          .map(s => ({ ...s, superieur_id: premierSuperieurVisible(s) }));
        const orgTree = buildOrgTree(orgSource);
        const hasSubordinates = new Set();
        orgSource.forEach(s => { if (s.superieur_id) hasSubordinates.add(s.superieur_id); });
        const agregats = orgAgregats;
        const licenciementPar = {};
        licenciements.forEach(l => { if (l.statut !== 'annule') licenciementPar[l.salarie_id] = l; });

        const treeRoots = orgTree.filter(s => s.children.length > 0 || hasSubordinates.has(s.id));
        const unattached = orgTree.filter(s => s.children.length === 0 && !hasSubordinates.has(s.id) && !s.superieur_id);

        // — Helpers DnD —
        // Calcule une fois par glissement : appeler ceci a chaque mousemove
        // reconstruisait tout l'arbre (115 salaries) des dizaines de fois par seconde.
        const calculerDescendants = (id) => {
          const enfantsPar = {};
          salaries.forEach(s => { if (s.superieur_id) (enfantsPar[s.superieur_id] = enfantsPar[s.superieur_id] || []).push(s.id); });
          const vus = new Set();
          const pile = [id];
          while (pile.length) {
            const cur = pile.pop();
            (enfantsPar[cur] || []).forEach(c => { if (!vus.has(c)) { vus.add(c); pile.push(c); } });
          }
          return vus;
        };
        // true si `cible` est un descendant du noeud en cours de glissement (boucle interdite)
        const estDescendantDuGlisse = (cibleId) => !!orgDescendants.current && orgDescendants.current.has(cibleId);

        const handleDragStart = (e, id) => {
          setOrgDragId(id);
          orgDescendants.current = calculerDescendants(id);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', id);
        };

        const handleDragEnd = () => {
          setOrgDragId(null);
          setOrgDropTarget(null);
          orgDescendants.current = null;
          arreterAutoScroll();
        };

        const handleDragOver = (e, id, position) => {
          e.preventDefault();
          e.stopPropagation();
          if (orgDragId === id) return;
          setOrgDropTarget({ id, position });
        };

        const handleDragLeave = (e) => {
          e.stopPropagation();
          // Ne clear que si on quitte vraiment la carte
          const related = e.relatedTarget;
          if (related && e.currentTarget.contains(related)) return;
          setOrgDropTarget(null);
        };

        const refreshSalaries = async () => {
          const params = { exercice_id: selectedExercice, ...filtreSociete, activite_id: selectedActivite !== 'global' ? selectedActivite : undefined, search: search || undefined };
          const sal = await salairesApi.getSalaries(params);
          setSalaries(sal.data);
          salairesApi.getSalaries({ ...filtreSociete }).then(r => setAllSalaries(r.data)).catch(() => {});
        };

        const handleDropOnCard = async (e, targetId) => {
          e.preventDefault();
          e.stopPropagation();
          setOrgDropTarget(null);
          if (!orgDragId || orgDragId === targetId) return;
          if (estDescendantDuGlisse(targetId)) return; // empêcher boucle
          const deplaceId = orgDragId;
          const ancienSup = salaries.find(x => x.id === deplaceId)?.superieur_id || null;
          try {
            await salairesApi.updateSalarie(deplaceId, { superieur_id: targetId });
            showToast('Collaborateur rattache', 'success', async () => {
              try { await salairesApi.updateSalarie(deplaceId, { superieur_id: ancienSup }); await refreshSalaries(); }
              catch { showToast('Erreur annulation', 'error'); }
            });
            await refreshSalaries();
          } catch { showToast('Erreur deplacement', 'error'); }
          setOrgDragId(null);
        };

        // groupServiceId : undefined = ne pas toucher au service ; null = sortir du service ; id = y entrer
        const handleDropBetween = async (e, siblingId, position, parentId, groupServiceId) => {
          e.preventDefault();
          e.stopPropagation();
          setOrgDropTarget(null);
          if (!orgDragId || orgDragId === siblingId) return;

          // Les freres sont ceux REELLEMENT affiches : orgSource exclut les contrats
          // echus et reparente les masques. Partir de `salaries` renumerotait
          // ordre_org sur un ensemble different de celui que l'utilisateur voit.
          const siblings = (parentId
            ? orgSource.filter(s => s.superieur_id === parentId)
            : treeRoots
          ).slice().sort((a, b) => (a.ordre_org || 0) - (b.ordre_org || 0));

          // Retirer le dragged s'il est déjà dans cette liste
          const filtered = siblings.filter(s => s.id !== orgDragId);
          const targetIdx = filtered.findIndex(s => s.id === siblingId);
          const insertIdx = position === 'before' ? targetIdx : targetIdx + 1;
          filtered.splice(insertIdx, 0, { id: orgDragId });

          // Construire items de reorder
          const items = filtered.map((s, i) => ({ id: s.id, ordre_org: i }));
          // Si le dragged vient d'un autre parent, changer aussi son superieur_id
          const dragged = salaries.find(s => s.id === orgDragId);
          const draggedParent = dragged?.superieur_id || null;
          if (draggedParent !== (parentId || null)) {
            items.find(it => it.id === orgDragId).superieur_id = parentId || null;
          }
          // Le service suit le bloc dans lequel on depose
          if (groupServiceId !== undefined && String(dragged?.service_id || '') !== String(groupServiceId || '')) {
            items.find(it => it.id === orgDragId).service_id = groupServiceId;
          }

          // Etat anterieur des memes lignes, pour l'annulation
          const itemsAvant = items.map(it => {
            const ancien = salaries.find(x => x.id === it.id);
            const a = { id: it.id, ordre_org: ancien?.ordre_org || 0 };
            if (it.superieur_id !== undefined) a.superieur_id = ancien?.superieur_id || null;
            if (it.service_id !== undefined) a.service_id = ancien?.service_id || null;
            return a;
          });

          try {
            await salairesApi.reorderOrg(items);
            showToast('Ordre mis a jour', 'success', async () => {
              try { await salairesApi.reorderOrg(itemsAvant); await refreshSalaries(); }
              catch { showToast('Erreur annulation', 'error'); }
            });
            await refreshSalaries();
          } catch { showToast('Erreur reordonnancement', 'error'); }
          setOrgDragId(null);
        };

        const renderOrgNode = (node, parentId, groupServiceId) => {
          const isVertical = node.orientation_org === 'vertical';
          const isDragging = orgDragId === node.id;
          const isDropOn = orgDropTarget?.id === node.id && orgDropTarget?.position === 'on';
          const isDropBefore = orgDropTarget?.id === node.id && orgDropTarget?.position === 'before';
          const isDropAfter = orgDropTarget?.id === node.id && orgDropTarget?.position === 'after';

          return (
            <div className={`org-node ${isDragging ? 'org-dragging' : ''}`} key={node.id}>
              {isAdmin && <div className={`org-drop-zone org-drop-before ${isDropBefore ? 'active' : ''}`}
                onDragOver={e => handleDragOver(e, node.id, 'before')}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDropBetween(e, node.id, 'before', parentId, groupServiceId)} />}
              <div className={`org-card ${isDropOn ? 'org-drop-highlight' : ''}`}
                draggable={isAdmin}
                onDragStart={e => handleDragStart(e, node.id)}
                onDragEnd={handleDragEnd}
                onDragOver={e => handleDragOver(e, node.id, 'on')}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDropOnCard(e, node.id)}
                onClick={() => !orgDragId && openOrgConfig(node.id)}
                onMouseEnter={e => survolerCarte(e, node.id)}
                onMouseLeave={quitterCarte}
                style={node.couleur_org ? { background: node.couleur_org, borderColor: node.couleur_org } : undefined}>
                <div className="org-card-name">{node.nom} {node.prenom || ''}</div>
                {node.poste_libelle && <div className="org-card-poste">{node.poste_libelle}</div>}
                {orgChamps.includes('alertes') && ((isAdmin && licenciementPar[node.id]) || echeanceProche(node.date_fin_contrat)) && (
                  <div className="org-card-badges">
                    {isAdmin && licenciementPar[node.id] && (
                      <span className="org-badge licenciement"
                        title={`Licenciement ${licenciementPar[node.id].statut}${licenciementPar[node.id].date_envisagee ? ' — ' + dateFr(licenciementPar[node.id].date_envisagee) : ''}`}>
                        Licenciement
                      </span>
                    )}
                    {echeanceProche(node.date_fin_contrat) && (
                      <span className="org-badge echeance" title={`Fin de contrat le ${dateFr(node.date_fin_contrat)}`}>
                        Fin {dateFr(node.date_fin_contrat)}
                      </span>
                    )}
                  </div>
                )}
                {champsActifs.length > 0 && (
                  <div className="org-card-infos">
                    {champsActifs.map(c => {
                      const v = c.valeur(node, orgMetrics[node.id], agregats[node.id]);
                      return (
                        <div key={c.cle} className={`org-card-info ${v ? '' : 'vide'}`}>
                          <span className="org-card-info-cle">{c.court}</span>
                          <span className="org-card-info-val">{v || '—'}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {isAdmin && (
                  <button className="org-card-add-bloc" title="Creer un bloc sous ce collaborateur (ex. Vendeurs comptoir)"
                    onClick={e => { e.stopPropagation(); setOrgBlocForm({ parentId: node.id, libelle: '' }); }}>
                    + Bloc
                  </button>
                )}
              </div>
              {isAdmin && <div className={`org-drop-zone org-drop-after ${isDropAfter ? 'active' : ''}`}
                onDragOver={e => handleDragOver(e, node.id, 'after')}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDropBetween(e, node.id, 'after', parentId, groupServiceId)} />}
              {(() => {
                // Grouper par service
                const childrenByService = {};
                const noService = [];
                node.children.forEach(child => {
                  if (child.service_id && child.service_libelle) {
                    if (!childrenByService[child.service_id]) childrenByService[child.service_id] = { libelle: child.service_libelle, children: [], ordre: 0 };
                    childrenByService[child.service_id].children.push(child);
                    // Prendre l'ordre du service depuis la liste services
                    const svc = services.find(sv => sv.id === child.service_id);
                    if (svc) childrenByService[child.service_id].ordre = svc.ordre || 0;
                  } else {
                    noService.push(child);
                  }
                });
                // Blocs ancres sur ce N+1 : affiches meme vides, prets a recevoir
                // des collaborateurs par glisser-deposer.
                services.forEach(sv => {
                  if (String(sv.org_parent_salarie_id || '') !== String(node.id)) return;
                  if (!childrenByService[sv.id]) childrenByService[sv.id] = { libelle: sv.libelle, children: [], ordre: sv.ordre || 0 };
                });
                const serviceGroups = Object.entries(childrenByService).sort((a, b) => a[1].ordre - b[1].ordre);
                const blocEnCreation = orgBlocForm && orgBlocForm.parentId === node.id;
                if (node.children.length === 0 && serviceGroups.length === 0 && !blocEnCreation) return null;

                return (
                  <div className={`org-children ${isVertical ? 'org-children-vertical' : ''}`}>
                    {noService.map(child => renderOrgNode(child, node.id, null))}
                    {serviceGroups.map(([svcId, group], idx) => {
                      const hue = Array.from(group.libelle).reduce((h, c) => c.charCodeAt(0) + ((h << 5) - h), 0) % 360;
                      const dragKey = `${node.id}-${svcId}`;
                      const isGroupDragging = orgDragServiceKey === dragKey;
                      const isGroupDropTarget = orgDropServiceKey === dragKey;
                      // Orientation propre au service ; vide/absente => on herite de celle du N+1
                      const svcObj = services.find(sv => String(sv.id) === String(svcId));
                      const svcOrientation = (svcObj && svcObj.orientation_org) || '';
                      const isServiceVertical = svcOrientation ? svcOrientation === 'vertical' : isVertical;
                      // Largeur : apercu pendant le glissement, sinon valeur stockee, sinon auto
                      const enRedim = orgResizeSize && String(orgResizeSize.svcId) === String(svcId);
                      const largeurEffective = enRedim ? orgResizeSize.width : ((svcObj && svcObj.largeur_org) || null);
                      // hauteur = minimum : le contenu peut toujours la depasser, jamais deborder
                      const hauteurEffective = enRedim ? orgResizeSize.height : ((svcObj && svcObj.hauteur_org) || null);
                      return (
                        <div key={`svc-${svcId}`}
                          className={`org-service-group ${isGroupDragging ? 'org-service-dragging' : ''} ${isGroupDropTarget ? 'org-service-drop-target' : ''} ${enRedim ? 'org-service-resizing' : ''}`}
                          style={{ background: `hsl(${hue}, 40%, 95%)`, borderColor: `hsl(${hue}, 40%, 80%)`, width: largeurEffective ? `${largeurEffective}px` : undefined, minHeight: hauteurEffective ? `${hauteurEffective}px` : undefined }}
                          onContextMenu={e => {
                            if (!isAdmin) return;
                            e.preventDefault();
                            e.stopPropagation(); // le bloc le plus imbrique l'emporte
                            setOrgServiceMenu({ svcId: parseInt(svcId, 10), libelle: group.libelle, current: svcOrientation, x: e.clientX, y: e.clientY, parentId: node.id });
                          }}
                          draggable={isAdmin && !enRedim}
                          onDragStart={e => { e.stopPropagation(); setOrgDragServiceKey(dragKey); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragEnd={() => { setOrgDragServiceKey(null); setOrgDropServiceKey(null); }}
                          onDragOver={e => {
                            e.preventDefault(); e.stopPropagation();
                            if (orgDragServiceKey) {
                              // reordonnancement des blocs entre eux
                              if (orgDragServiceKey !== dragKey && orgDragServiceKey.startsWith(node.id + '-')) setOrgDropServiceKey(dragKey);
                            } else if (orgDragId && orgDragId !== node.id && !estDescendantDuGlisse(node.id)) {
                              // un collaborateur entre dans le service
                              setOrgDropServiceKey(dragKey);
                            }
                          }}
                          onDragLeave={e => { e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget)) setOrgDropServiceKey(null); }}
                          onDrop={async e => {
                            e.preventDefault(); e.stopPropagation();
                            setOrgDropServiceKey(null);
                            // Cas 1 : un collaborateur est depose dans le bloc — il rejoint ce
                            // service, rattache au meme N+1 que les autres membres du bloc.
                            if (orgDragId) {
                              const dragged = orgDragId;
                              setOrgDragId(null);
                              if (dragged === node.id || estDescendantDuGlisse(node.id)) return;
                              const avant = salaries.find(x => x.id === dragged);
                              const retour = { superieur_id: avant?.superieur_id || null, service_id: avant?.service_id || null };
                              try {
                                await salairesApi.updateSalarie(dragged, { superieur_id: node.id, service_id: parseInt(svcId, 10) });
                                showToast(`Rattache au service ${group.libelle}`, 'success', async () => {
                                  try { await salairesApi.updateSalarie(dragged, retour); await refreshSalaries(); }
                                  catch { showToast('Erreur annulation', 'error'); }
                                });
                                await refreshSalaries();
                              } catch { showToast('Erreur rattachement', 'error'); }
                              return;
                            }
                            // Cas 2 : reordonnancement des blocs service entre eux
                            if (!orgDragServiceKey || orgDragServiceKey === dragKey) return;
                            const dragSvcId = parseInt(orgDragServiceKey.split('-')[1]);
                            // Réordonner les services : swap les ordres
                            const currentOrder = serviceGroups.map(([id]) => parseInt(id));
                            const fromIdx = currentOrder.indexOf(dragSvcId);
                            const toIdx = currentOrder.indexOf(parseInt(svcId));
                            if (fromIdx === -1 || toIdx === -1) return;
                            currentOrder.splice(fromIdx, 1);
                            currentOrder.splice(toIdx, 0, dragSvcId);
                            try {
                              for (let i = 0; i < currentOrder.length; i++) {
                                const sv = services.find(s => s.id === currentOrder[i]);
                                if (sv && sv.ordre !== i) {
                                  await plApi.updateService(currentOrder[i], { ordre: i });
                                }
                              }
                              // Recharger les services
                              const svRes = await plApi.getServices();
                              setServices(svRes.data);
                              showToast('Ordre des services mis a jour');
                            } catch { showToast('Erreur reordonnancement', 'error'); }
                            setOrgDragServiceKey(null);
                          }}>
                          <div className="org-service-label" style={{ color: `hsl(${hue}, 50%, 35%)` }} title={isAdmin ? 'Clic droit : renommer, supprimer, orientation' : undefined}>{group.libelle}</div>
                          <div className={`org-service-children ${isServiceVertical ? 'org-children-vertical' : ''}`}>
                            {group.children.map(child => renderOrgNode(child, node.id, parseInt(svcId, 10)))}
                            {group.children.length === 0 && (
                              <div className="org-service-vide-hint">Glissez des collaborateurs ici</div>
                            )}
                          </div>
                          {isAdmin && (() => {
                            const demarrerRedim = (axis) => (e) => {
                              e.preventDefault();  // empeche le drag HTML5 du bloc de demarrer
                              e.stopPropagation();
                              const bloc = e.currentTarget.parentElement;
                              const id = parseInt(svcId, 10);
                              orgResizeRef.current = {
                                svcId: id, axis,
                                startX: e.clientX, startY: e.clientY,
                                startWidth: bloc.offsetWidth, startHeight: bloc.offsetHeight,
                                width: bloc.offsetWidth, height: bloc.offsetHeight,
                              };
                              setOrgResizeSize({ svcId: id, width: bloc.offsetWidth, height: bloc.offsetHeight });
                            };
                            return (
                              <>
                                <div className="org-service-resize org-resize-x" title="Glisser : largeur du bloc"
                                  onMouseDown={demarrerRedim('x')} />
                                <div className="org-service-resize org-resize-y" title="Glisser : hauteur du bloc"
                                  onMouseDown={demarrerRedim('y')} />
                                <div className="org-service-resize org-resize-xy" title="Glisser : largeur et hauteur"
                                  onMouseDown={demarrerRedim('xy')} />
                              </>
                            );
                          })()}
                        </div>
                      );
                    })}
                    {blocEnCreation && (
                      <form className="org-bloc-form" onSubmit={e => { e.preventDefault(); creerBloc(); }}>
                        <input autoFocus placeholder="Nom du bloc (ex. Vendeurs comptoir)"
                          value={orgBlocForm.libelle}
                          onChange={e => setOrgBlocForm(f => ({ ...f, libelle: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Escape') setOrgBlocForm(null); }} />
                        <button type="submit">Creer</button>
                        <button type="button" onClick={() => setOrgBlocForm(null)}>Annuler</button>
                      </form>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        };

        const champsActifs = champsAutorises.filter(c => orgChamps.includes(c.cle));
        const libelleMoisReels = orgMoisReels.length
          ? `${orgMoisReels.length} mois reel${orgMoisReels.length > 1 ? 's' : ''} (${orgMoisReels.map(m => String(m.mois).padStart(2, '0') + '/' + m.annee).join(', ')})`
          : 'aucun mois marque reel';

        return (
          <div className={`org-container ${champsActifs.length ? 'org-cartes-riches' : ''}`}>
            <div className="org-champs-barre">
              <span className="org-champs-titre">Afficher sur les cartes</span>
              {champsAutorises.map(c => (
                <button key={c.cle}
                  className={`org-champ-chip ${orgChamps.includes(c.cle) ? 'active' : ''}`}
                  onClick={() => basculerChamp(c.cle)}>
                  {c.label}
                </button>
              ))}
              <button className={`org-champ-chip ${orgChamps.includes('alertes') ? 'active' : ''}`}
                title="Badges licenciement et fin de contrat sur les cartes"
                onClick={() => basculerChamp('alertes')}>
                Alertes (licenciement, fin de contrat)
              </button>
              {orgChamps.length > 0 && (
                <button className="org-champ-reset" onClick={() => setOrgChamps([])}>Tout masquer</button>
              )}
              <span className="org-champs-note" title="Les montants ne sont calcules que sur les mois marques « reel »">
                Base de calcul : {libelleMoisReels}
              </span>
            </div>
            {treeRoots.length === 0 && unattached.length === salaries.length && (
              <div className="org-empty">
                <p>Aucun superieur hierarchique n'a ete defini.</p>
                <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>Rendez-vous dans la "Liste des salaries" pour attribuer un superieur a chaque salarie, ou glissez-deposez depuis "Non rattaches".</p>
              </div>
            )}
            {treeRoots.length > 0 && (
              <div className="org-tree-scroll" ref={orgScrollRef} onDragOverCapture={surveillerAutoScroll}>
                <div className="org-tree">
                  {treeRoots.map(root => renderOrgNode(root, null))}
                </div>
              </div>
            )}
            {masques.length > 0 && (
              <div className="org-masques">
                <h4>Masques de l'organigramme ({masques.length})</h4>
                <div className="org-masques-list">
                  {masques.map(s => (
                    <div className="org-masque-chip" key={s.id}>
                      <span className="org-masque-nom">{s.nom} {s.prenom || ''}</span>
                      {isAdmin && (
                        <button className="org-masque-restore" title="Reafficher dans l'organigramme"
                          onClick={() => masquerOrganigramme(s.id, false)}>Reafficher</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(unattached.length > 0 || orgDragId) && (
              <div className={`org-unattached ${orgUnattachedDrop ? 'org-unattached-drop' : ''}`}
                onDragOver={e => { if (!orgDragId) return; e.preventDefault(); e.stopPropagation(); setOrgUnattachedDrop(true); }}
                onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOrgUnattachedDrop(false); }}
                onDrop={async e => {
                  e.preventDefault(); e.stopPropagation();
                  setOrgUnattachedDrop(false);
                  const dragged = orgDragId;
                  setOrgDragId(null);
                  if (!dragged) return;
                  const cur = salaries.find(x => x.id === dragged);
                  if (!cur || !cur.superieur_id) return; // deja detache
                  await detacherSuperieur(dragged);
                }}>
                <h4>Non rattaches ({unattached.length})</h4>
                <div className="org-unattached-list">
                  {unattached.map(s => (
                    <div className={`org-card org-card-small ${orgDragId === s.id ? 'org-dragging' : ''}`} key={s.id}
                      draggable={isAdmin}
                      onDragStart={e => handleDragStart(e, s.id)}
                      onDragEnd={handleDragEnd}
                      onClick={() => !orgDragId && openOrgConfig(s.id)}
                      onMouseEnter={e => survolerCarte(e, s.id)}
                      onMouseLeave={quitterCarte}
                      style={s.couleur_org ? { background: s.couleur_org, borderColor: s.couleur_org } : undefined}>
                      <div className="org-card-name">{s.nom} {s.prenom || ''}</div>
                      {s.poste_libelle && <div className="org-card-poste">{s.poste_libelle}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {activeView === 'masse' && isAdmin && <MasseSalariale isAdmin={isAdmin} />}

      {/* Onglet Evolution effectifs */}
      {activeView === 'evolution' && (() => {
        const exObj = exercices.find(e => String(e.id) === String(selectedExercice));
        const annee = exObj ? parseInt(exObj.annee) : new Date().getFullYear();
        // Parse YYYY-MM-DD en date locale (évite le décalage UTC)
        const parseLocal = (str) => {
          if (!str) return null;
          const [y, m, d] = str.substring(0, 10).split('-').map(Number);
          return new Date(y, m - 1, d);
        };
        const nomComplet = s => `${s.prenom || ''} ${s.nom || ''}`.trim();
        const effectifsParMois = MOIS.map((moisLabel, idx) => {
          const debutMois = new Date(annee, idx, 1);
          const finMois = new Date(annee, idx + 1, 0); // dernier jour du mois
          const presents = salaries.filter(s => {
            const embauche = parseLocal(s.date_anciennete);
            const fin = parseLocal(s.date_fin_contrat);
            if (embauche && embauche > finMois) return false;
            if (fin && fin < debutMois) return false;
            if (!embauche && fin) return false;
            return true;
          });
          const entrees = salaries.filter(s => {
            const d = parseLocal(s.date_anciennete);
            if (!d) return false;
            return d >= debutMois && d <= finMois;
          });
          const sorties = salaries.filter(s => {
            const d = parseLocal(s.date_fin_contrat);
            if (!d) return false;
            return d >= debutMois && d <= finMois;
          });
          return { mois: moisLabel, count: presents.length, entrees: entrees.map(nomComplet), sorties: sorties.map(nomComplet) };
        });
        const maxCount = Math.max(...effectifsParMois.map(e => e.count), 1);
        return (
          <div className="evolution-container">
            <h3 className="evolution-title">Evolution des effectifs — {annee}</h3>
            <div className="evolution-chart">
              {effectifsParMois.map((e, i) => (
                <div key={i} className="evolution-bar-group">
                  <div className="evolution-bar-value">{e.count}</div>
                  <div className="evolution-bar-wrapper">
                    <div className="evolution-bar" style={{ height: `${(e.count / maxCount) * 100}%` }} />
                  </div>
                  <div className="evolution-bar-label">{e.mois}</div>
                </div>
              ))}
            </div>
            <div className="pl-table-container" style={{ marginTop: '1.5rem' }}>
              <table className="pl-salaries-table annuaire-table">
                <thead>
                  <tr>
                    <th>Mois</th>
                    <th>Effectif</th>
                    <th>Variation</th>
                    <th>Flux</th>
                  </tr>
                </thead>
                <tbody>
                  {effectifsParMois.map((e, i) => {
                    const prev = i > 0 ? effectifsParMois[i - 1].count : e.count;
                    const diff = e.count - prev;
                    return (
                      <tr key={i}>
                        <td>{e.mois} {annee}</td>
                        <td>{e.count}</td>
                        <td style={{ color: diff > 0 ? '#16a34a' : diff < 0 ? '#dc2626' : '#6b7280' }}>
                          {i === 0 ? '-' : (diff > 0 ? `+${diff}` : diff === 0 ? '=' : diff)}
                        </td>
                        <td className="evolution-flux-cell">
                          {e.entrees.map((n, j) => <span key={`e${j}`} className="evolution-flux-tag entree">{n}</span>)}
                          {e.sorties.map((n, j) => <span key={`s${j}`} className="evolution-flux-tag sortie">{n}</span>)}
                          {e.entrees.length === 0 && e.sorties.length === 0 && <span className="evolution-flux-empty">-</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Onglet Licenciements envisagés */}
      {activeView === 'licenciements' && isAdmin && (
        <div className="evolution-container">
          <div className="licenciement-add-form">
            <select value={licAddSalarieId} onChange={e => setLicAddSalarieId(e.target.value)}>
              <option value="">-- Selectionner un salarie --</option>
              {salaries.filter(s => !s.date_fin_contrat && !licenciements.some(l => l.salarie_id === s.id && l.statut !== 'annule')).map(s => (
                <option key={s.id} value={s.id}>{s.nom} {s.prenom}{s.activite_libelle ? ` (${s.activite_libelle})` : ''}</option>
              ))}
            </select>
            <input value={licAddMotif} onChange={e => setLicAddMotif(e.target.value)} placeholder="Motif" />
            <input type="date" value={licAddDate} onChange={e => setLicAddDate(e.target.value)} title="Date envisagee" />
            <input value={licAddCommentaire} onChange={e => setLicAddCommentaire(e.target.value)} placeholder="Commentaire (optionnel)" />
            <button className="salaires-add-btn" disabled={!licAddSalarieId} onClick={async () => {
              try {
                await salairesApi.createLicenciement({ salarie_id: licAddSalarieId, motif: licAddMotif, date_envisagee: licAddDate || null, commentaire: licAddCommentaire });
                showToast('Licenciement ajoute');
                setLicAddSalarieId(''); setLicAddMotif(''); setLicAddDate(''); setLicAddCommentaire('');
                refreshLicenciements();
              } catch { showToast('Erreur', 'error'); }
            }}>Ajouter</button>
          </div>
          <div className="pl-table-container" style={{ marginTop: '1rem' }}>
            <table className="pl-salaries-table annuaire-table">
              <thead>
                <tr>
                  <th>Salarie</th>
                  <th>Societe</th>
                  <th>Activite</th>
                  <th>Poste</th>
                  <th>Motif</th>
                  <th>Date envisagee</th>
                  <th>Statut</th>
                  <th>Commentaire</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {licenciements.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Aucun licenciement envisage</td></tr>
                ) : licenciements.map(l => licEditingId === l.id ? (
                  <tr key={l.id} className="annuaire-editing-row">
                    <td>{l.nom} {l.prenom}</td>
                    <td>{l.societe_nom || '-'}</td>
                    <td>{l.activite_libelle || '-'}</td>
                    <td>{l.poste_libelle || '-'}</td>
                    <td><input value={licEditVal.motif} onChange={e => setLicEditVal(v => ({ ...v, motif: e.target.value }))} /></td>
                    <td><input type="date" value={licEditVal.date_envisagee} onChange={e => setLicEditVal(v => ({ ...v, date_envisagee: e.target.value }))} /></td>
                    <td>
                      <select value={licEditVal.statut} onChange={e => setLicEditVal(v => ({ ...v, statut: e.target.value }))}>
                        <option value="envisage">Envisage</option>
                        <option value="en_cours">En cours</option>
                        <option value="confirme">Confirme</option>
                        <option value="annule">Annule</option>
                      </select>
                    </td>
                    <td><input value={licEditVal.commentaire} onChange={e => setLicEditVal(v => ({ ...v, commentaire: e.target.value }))} /></td>
                    <td className="annuaire-actions">
                      <button className="annuaire-btn annuaire-btn-save" onClick={async () => {
                        try {
                          await salairesApi.updateLicenciement(l.id, licEditVal);
                          setLicEditingId(null);
                          showToast('Mis a jour');
                          refreshLicenciements();
                        } catch { showToast('Erreur', 'error'); }
                      }} title="Enregistrer">✓</button>
                      <button className="annuaire-btn annuaire-btn-cancel" onClick={() => setLicEditingId(null)} title="Annuler">✕</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={l.id} className={l.statut === 'annule' ? 'annuaire-row-inactif' : ''}>
                    <td>{l.nom} {l.prenom}</td>
                    <td>{l.societe_nom || '-'}</td>
                    <td>{l.activite_libelle || '-'}</td>
                    <td>{l.poste_libelle || '-'}</td>
                    <td>{l.motif || '-'}</td>
                    <td>{l.date_envisagee ? new Date(l.date_envisagee).toLocaleDateString('fr-FR') : '-'}</td>
                    <td><span className={`licenciement-statut ${l.statut}`}>
                      {l.statut === 'envisage' ? 'Envisage' : l.statut === 'en_cours' ? 'En cours' : l.statut === 'confirme' ? 'Confirme' : 'Annule'}
                    </span></td>
                    <td>{l.commentaire || '-'}</td>
                    <td className="annuaire-actions">
                      <button className="annuaire-btn annuaire-btn-edit" onClick={() => {
                        setLicEditingId(l.id);
                        setLicEditVal({ motif: l.motif || '', date_envisagee: l.date_envisagee ? l.date_envisagee.substring(0, 10) : '', statut: l.statut, commentaire: l.commentaire || '' });
                      }} title="Modifier">✎</button>
                      <button className="pl-config-delete" onClick={async () => {
                        if (!window.confirm('Supprimer ce licenciement envisage ?')) return;
                        await salairesApi.deleteLicenciement(l.id);
                        showToast('Supprime');
                        refreshLicenciements();
                      }}>x</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Étape 1 : confirmation du mois */}
      {pdfStep === 'confirm-mois' && pdfPreview && (
        <div className="pdf-modal-overlay" onClick={handlePdfCancel}>
          <div className="pdf-confirm-mois-modal" onClick={e => e.stopPropagation()}>
            <h3>Import bulletins de paie</h3>
            <p className="pdf-confirm-count">
              {pdfPreview.length} bulletin(s) detecte(s)
              {pdfInfos?.parFichier?.length > 1 && ` dans ${pdfInfos.parFichier.length} fichiers`}
            </p>
            {pdfInfos?.parFichier?.length > 1 && (
              <ul className="pdf-fichiers-liste">
                {pdfInfos.parFichier.map(f => (
                  <li key={f.nom}>{f.nom} — {f.bulletins} bulletin(s)</li>
                ))}
              </ul>
            )}
            {pdfInfos?.periodes?.length > 1 && (
              <div className="pdf-alerte">
                Periodes differentes detectees ({pdfInfos.periodes.join(', ')}). Tout le lot sera
                enregistre sur le seul mois choisi ci-dessous : importez un mois a la fois.
              </div>
            )}
            {pdfInfos?.doublons?.length > 0 && (
              <div className="pdf-alerte">
                Matricule(s) present(s) dans plusieurs fichiers, la derniere valeur l'emportera :
                {' '}{pdfInfos.doublons.join(' · ')}
              </div>
            )}
            {pdfInfos?.echecs?.length > 0 && (
              <div className="pdf-alerte">
                Fichier(s) non lus : {pdfInfos.echecs.join(' · ')}
              </div>
            )}
            <div className="pdf-confirm-mois-fields">
              <div className="pl-filter-group">
                <label>Mois</label>
                <select value={pdfImportMois} onChange={e => setPdfImportMois(parseInt(e.target.value))}>
                  {MOIS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div className="pl-filter-group">
                <label>Annee</label>
                <select value={pdfImportYear} onChange={e => setPdfImportYear(parseInt(e.target.value))}>
                  {exercices.map(ex => <option key={ex.id} value={ex.annee}>{ex.annee}</option>)}
                </select>
              </div>
            </div>
            <div className="pdf-confirm-mois-display">
              {MOIS[pdfImportMois - 1]} {pdfImportYear}
            </div>
            <div className="pdf-confirm-mois-actions">
              <button className="pdf-confirm-btn" onClick={handlePdfContinueToPreview}>Continuer</button>
              <button className="btn-cancel" onClick={handlePdfCancel}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Étape 2 : preview tableau */}
      {pdfStep === 'preview' && pdfPreview && (
        <div className="pdf-modal-overlay" onClick={handlePdfCancel}>
          <div className="pdf-modal" onClick={e => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <h3>Import — {MOIS[pdfImportMois - 1]} {pdfImportYear}</h3>
              <span className="pdf-badge-count">{pdfPreview.length} bulletin(s)</span>
            </div>

            <div className="pdf-modal-body">
              <table className="pdf-preview-table">
                <thead>
                  <tr>
                    <th>N° Salarie</th>
                    <th>Nom Prenom</th>
                    <th>Sal. Brut</th>
                    <th>Cotis. Sal.</th>
                    <th>Cotis. Patr.</th>
                    <th>Total Verse</th>
                    <th>Lignes</th>
                  </tr>
                </thead>
                <tbody>
                  {pdfPreview.map(b => {
                    const known = salaries.find(s => s.numero_salarie === b.numero_salarie);
                    const isUnknown = !known;
                    const expanded = pdfExpandedRows.has(b.numero_salarie);
                    return (
                      <React.Fragment key={b.numero_salarie}>
                        <tr className={isUnknown ? 'pdf-row-unknown' : 'pdf-row-known'} onClick={() => togglePdfRow(b.numero_salarie)}>
                          <td className="pdf-cell-numero">{b.numero_salarie}</td>
                          <td>
                            {known ? (
                              <span className="pdf-known-name">{known.nom} {known.prenom}</span>
                            ) : (
                              <div className="pdf-unknown-inputs" onClick={e => e.stopPropagation()}>
                                <input
                                  placeholder="Nom *"
                                  value={pdfUnknowns[b.numero_salarie]?.nom || ''}
                                  onChange={e => setPdfUnknowns(prev => ({ ...prev, [b.numero_salarie]: { ...prev[b.numero_salarie], nom: e.target.value } }))}
                                />
                                <input
                                  placeholder="Prenom"
                                  value={pdfUnknowns[b.numero_salarie]?.prenom || ''}
                                  onChange={e => setPdfUnknowns(prev => ({ ...prev, [b.numero_salarie]: { ...prev[b.numero_salarie], prenom: e.target.value } }))}
                                />
                              </div>
                            )}
                          </td>
                          <td className="pdf-cell-montant">{fmt(b.salaire_brut)}</td>
                          <td className="pdf-cell-montant">{fmt(b.cotisations_salariales)}</td>
                          <td className="pdf-cell-montant">{fmt(b.cotisations_patronales)}</td>
                          <td className="pdf-cell-montant pdf-cell-total">{fmt(b.total_verse)}</td>
                          <td className="pdf-cell-expand">{b.lignes.length} {expanded ? '▲' : '▼'}</td>
                        </tr>
                        {expanded && b.lignes.map((l, li) => (
                          <tr key={`${b.numero_salarie}-${li}`} className="pdf-detail-row">
                            <td></td>
                            <td className="pdf-detail-code">{l.code}</td>
                            <td colSpan={3} className="pdf-detail-libelle">{l.libelle}</td>
                            <td className={`pdf-cell-montant ${l.montant < 0 ? 'pdf-montant-neg' : ''}`}>
                              {Number(l.montant).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="pdf-detail-section">{l.section === 'brut' ? 'Brut' : 'Non soumis'}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pdf-modal-footer">
              <button
                className="pdf-confirm-btn"
                onClick={handlePdfConfirm}
                disabled={pdfImporting || Object.values(pdfUnknowns).some(v => !v.nom.trim())}
              >
                {pdfImporting ? 'Import en cours...' : `Confirmer ${MOIS[pdfImportMois - 1]} ${pdfImportYear}`}
              </button>
              <button className="btn-cancel" onClick={handlePdfCancel}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale config organigramme */}
      {orgConfigId && (() => {
        const s = salaries.find(x => x.id === orgConfigId);
        if (!s) return null;
        const orgConfigCurrent = s;
        const isVertical = orgConfigData.orientation_org === 'vertical';
        const COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];
        return (
          <div className="fiche-contrat-overlay" onClick={() => { saveOrgConfig(); setOrgConfigId(null); }}>
            <div className="org-config-modal" onClick={e => e.stopPropagation()}>
              <div className="fiche-contrat-header">
                <h3>{orgConfigCurrent.nom} {orgConfigCurrent.prenom || ''}</h3>
                <button className="fiche-close-btn" onClick={() => { saveOrgConfig(); setOrgConfigId(null); }}>&times;</button>
              </div>
              <div className="fiche-contrat-body">
                <div className="fiche-field" style={{ marginBottom: 16 }}>
                  <label>Couleur de carte</label>
                  <div className="org-color-picker">
                    <button className={`org-color-swatch ${!orgConfigData.couleur_org ? 'active' : ''}`} style={{ background: '#fff', border: '2px solid #d1d5db' }} onClick={() => setOrgConfigData(d => ({ ...d, couleur_org: '' }))} title="Par defaut" />
                    {COLORS.map(c => (
                      <button key={c} className={`org-color-swatch ${orgConfigData.couleur_org === c ? 'active' : ''}`} style={{ background: c }} onClick={() => setOrgConfigData(d => ({ ...d, couleur_org: c }))} />
                    ))}
                  </div>
                </div>
                {orgConfigCurrent.children?.length > 0 || salaries.some(x => x.superieur_id === orgConfigId) ? (
                  <div className="fiche-field" style={{ marginBottom: 16 }}>
                    <label>Orientation des N-1</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className={`org-orient-btn ${!isVertical ? 'active' : ''}`} onClick={() => setOrgConfigData(d => ({ ...d, orientation_org: 'horizontal' }))}>Horizontal</button>
                      <button className={`org-orient-btn ${isVertical ? 'active' : ''}`} onClick={() => setOrgConfigData(d => ({ ...d, orientation_org: 'vertical' }))}>Vertical</button>
                    </div>
                  </div>
                ) : null}
                <div className="fiche-field">
                  <label>Service</label>
                  <select value={orgConfigData.service_id || ''} onChange={e => setOrgConfigData(d => ({ ...d, service_id: e.target.value }))} disabled={!isAdmin}>
                    <option value="">Aucun service</option>
                    {services.filter(sv => !orgConfigCurrent.activite_id || String(sv.activite_id) === String(orgConfigCurrent.activite_id)).map(sv => <option key={sv.id} value={sv.id}>{sv.libelle}</option>)}
                  </select>
                </div>
                {isAdmin && orgConfigCurrent.superieur_id ? (
                  <div className="fiche-field" style={{ marginTop: 16 }}>
                    <label>Rattachement hierarchique</label>
                    <div className="org-detach-row">
                      <span className="org-detach-current">N+1 : {`${orgConfigCurrent.superieur_nom || ''} ${orgConfigCurrent.superieur_prenom || ''}`.trim() || '-'}</span>
                      <button className="org-detach-btn" onClick={async () => {
                        await detacherSuperieur(orgConfigId, orgConfigData);
                        setOrgConfigId(null);
                      }}>Detacher</button>
                    </div>
                    <div className="org-detach-hint">Le collaborateur repasse en "Non rattaches", ou devient une racine de l'organigramme s'il a des N-1.</div>
                  </div>
                ) : null}
                {isAdmin && (
                  <div className="fiche-field" style={{ marginTop: 16 }}>
                    <label>Presence dans l'organigramme</label>
                    <div className="org-detach-row">
                      <span className="org-detach-current">Visible</span>
                      <button className="org-detach-btn" onClick={async () => {
                        await masquerOrganigramme(orgConfigId, true, orgConfigData);
                        setOrgConfigId(null);
                      }}>Masquer</button>
                    </div>
                    <div className="org-detach-hint">Le salarie reste actif : paie, P&amp;L et liste des salaries inchanges. Ses N-1 sont rattaches a son N+1. Reversible depuis la zone "Masques" en bas de l'organigramme.</div>
                  </div>
                )}
              </div>
              <div className="fiche-contrat-footer">
                <button className="fiche-save-btn" onClick={() => { saveOrgConfig(); setOrgConfigId(null); }}>Enregistrer</button>
                <button className="fiche-cancel-btn" onClick={() => setOrgConfigId(null)}>Annuler</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Fiche au survol d'une carte de l'organigramme */}
      {orgSurvol && !orgDragId && (() => {
        const sal = salaries.find(x => x.id === orgSurvol.id);
        if (!sal) return null;
        const m = orgMetrics[sal.id] || {};
        const nbDirects = salaries.filter(x => x.superieur_id === sal.id).length;
        const agregatsSurvol = orgAgregats[sal.id];
        const licSurvol = licenciements.find(l => l.salarie_id === sal.id && l.statut !== 'annule');
        const lignes = [
          ['Poste', sal.poste_libelle],
          ['Qualification', sal.qualification],
          ['Societe', sal.societe_nom],
          ['Activite', sal.activite_libelle],
          ['Service', sal.service_libelle],
          ['Categorie', sal.categorie],
          ['N+1', `${sal.superieur_nom || ''} ${sal.superieur_prenom || ''}`.trim()],
          ['Subordonnes directs', nbDirects || null],
          ['Type de contrat', sal.type_contrat],
          ['Statut / CSP', sal.statut_contrat],
          ['Anciennete', ancienneteTexte(sal.date_anciennete, false)],
          ['Entree le', dateFr(sal.date_anciennete)],
          ['Fin de contrat prevue', dateFr(sal.date_fin_contrat)],
          ['Motif de fin', sal.motif_fin_contrat],
          ['Temps de travail', sal.temps_travail],
          ['Brut contractuel', sal.salaire_brut_contractuel ? `${fmt(sal.salaire_brut_contractuel)} €` : null, true],
          ['Variable contractuel', sal.variable_contractuel ? `${fmt(sal.variable_contractuel)} €` : null, true],
          ['Dernier salaire de base', m.salaire_base != null ? `${fmt(m.salaire_base)} € (${m.salaire_base_mois})` : null, true],
          ['Moyenne salaire brut (6 mois)', m.moyenne_brut != null ? `${fmt(m.moyenne_brut)} € sur ${m.nb_mois_brut} mois` : null, true],
          ['Cout employeur estime', m.cout_employeur != null ? `${fmt(m.cout_employeur)} €/mois` : null, true],
          ['Absences injustifiees', m.mois_injustifiees ? `${m.mois_injustifiees} mois sur ${orgMoisReels.length}` : null, true],
          ['Absences maladie', m.mois_maladie ? `${m.mois_maladie} mois sur ${orgMoisReels.length}` : null, true],
          ['Effectif de la branche', agregatsSurvol ? `${agregatsSurvol.effectif} personne${agregatsSurvol.effectif > 1 ? 's' : ''}` : null],
          ['Masse salariale branche', agregatsSurvol && agregatsSurvol.masse ? `${fmt(agregatsSurvol.masse)} €/mois` : null, true],
          ['Licenciement', licSurvol ? `${licSurvol.statut}${licSurvol.date_envisagee ? ' — ' + dateFr(licSurvol.date_envisagee) : ''}` : null, true],
        ].filter(([, v, sensible]) => (isAdmin || !sensible) && v !== null && v !== undefined && v !== '');
        return (
          <div className="org-survol" style={{
            left: Math.min(orgSurvol.x + 18, window.innerWidth - 340),
            top: Math.min(orgSurvol.y + 18, Math.max(12, window.innerHeight - 40 - lignes.length * 22 - 60)),
          }}>
            <div className="org-survol-titre">
              {sal.nom} {sal.prenom || ''}
              {sal.numero_salarie && <span className="org-survol-matricule">n° {sal.numero_salarie}</span>}
            </div>
            <div className="org-survol-corps">
              {lignes.map(([k, v]) => (
                <div className="org-survol-ligne" key={k}>
                  <span className="org-survol-cle">{k}</span>
                  <span className="org-survol-val">{v}</span>
                </div>
              ))}
            </div>
            {isAdmin && m.moyenne_brut == null && (
              <div className="org-survol-note">Aucun montant sur les mois marques « reel »</div>
            )}
          </div>
        );
      })()}

      {/* Menu contextuel : orientation des collaborateurs d'un service */}
      {orgServiceMenu && (
        <>
          <div className="org-ctx-backdrop"
            onClick={() => setOrgServiceMenu(null)}
            onContextMenu={e => { e.preventDefault(); setOrgServiceMenu(null); }} />
          <div className="org-ctx-menu" style={{ top: Math.min(orgServiceMenu.y, window.innerHeight - 170), left: Math.min(orgServiceMenu.x, window.innerWidth - 210) }}>
            <div className="org-ctx-title">{orgServiceMenu.libelle}</div>
            <div className="org-ctx-subtitle">Orientation des collaborateurs</div>
            {[
              { val: 'horizontal', label: 'Horizontal' },
              { val: 'vertical', label: 'Vertical' },
              { val: '', label: 'Heriter du N+1' },
            ].map(opt => (
              <button key={opt.val || 'inherit'}
                className={`org-ctx-item ${orgServiceMenu.current === opt.val ? 'active' : ''}`}
                onClick={() => setServiceOrientation(orgServiceMenu.svcId, opt.val)}>
                {opt.label}
              </button>
            ))}
            <div className="org-ctx-sep" />
            <button className="org-ctx-item" onClick={() => setServiceTailleAuto(orgServiceMenu.svcId, 'largeur_org')}>
              Largeur automatique
            </button>
            <button className="org-ctx-item" onClick={() => setServiceTailleAuto(orgServiceMenu.svcId, 'hauteur_org')}>
              Hauteur automatique
            </button>
            <div className="org-ctx-sep" />
            {orgServiceMenu.renaming ? (
              <form className="org-ctx-rename" onSubmit={e => { e.preventDefault(); renommerBloc(orgServiceMenu.svcId, orgServiceMenu.libelleEdit); }}>
                <input autoFocus value={orgServiceMenu.libelleEdit}
                  onChange={e => setOrgServiceMenu(m => ({ ...m, libelleEdit: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Escape') setOrgServiceMenu(m => ({ ...m, renaming: false })); }} />
                <button type="submit">OK</button>
              </form>
            ) : (
              <button className="org-ctx-item"
                onClick={() => setOrgServiceMenu(m => ({ ...m, renaming: true, libelleEdit: m.libelle }))}>
                Renommer le bloc
              </button>
            )}
            <button className="org-ctx-item org-ctx-danger"
              onClick={() => dissoudreBloc(orgServiceMenu.svcId, orgServiceMenu.parentId, orgServiceMenu.libelle)}>
              Supprimer le bloc (les membres restent)
            </button>
          </div>
        </>
      )}

      {/* Modale fiche contrat */}
      {ficheContratSalarieId && ficheCurrent && (
        <div className="fiche-contrat-overlay" onClick={async () => { await saveFicheContrat(); setFicheContratSalarieId(null); }}>
          <div className="fiche-contrat-modal" onClick={e => e.stopPropagation()}>
            <div className="fiche-contrat-header">
              <button className="fiche-nav-btn" disabled={ficheCurrentIndex <= 0} onClick={() => navigateFiche(-1)} title="Precedent">&larr;</button>
              <h3>{ficheCurrent.nom} {ficheCurrent.prenom} {ficheCurrent.numero_salarie ? `(${ficheCurrent.numero_salarie})` : ''}</h3>
              <button className="fiche-nav-btn" disabled={ficheCurrentIndex >= ficheFilteredList.length - 1} onClick={() => navigateFiche(1)} title="Suivant">&rarr;</button>
              <button className="fiche-close-btn" onClick={async () => { await saveFicheContrat(); setFicheContratSalarieId(null); }} title="Fermer">&times;</button>
            </div>

            <div className="fiche-contrat-info">
              <span>{ficheCurrent.societe_nom || '-'}</span>
              <span>{ficheCurrent.activite_libelle || '-'}</span>
              <span>{ficheCurrent.poste_libelle || '-'}</span>
              <span>{ficheCurrent.date_anciennete ? new Date(ficheCurrent.date_anciennete).toLocaleDateString('fr-FR') : '-'}</span>
            </div>

            <div className="fiche-contrat-body">
              <div className="fiche-contrat-fields">
                <div className="fiche-field">
                  <label>Statut</label>
                  <select value={ficheContratData.statut_contrat || ''} onChange={e => setFicheContratData(d => ({ ...d, statut_contrat: e.target.value }))} disabled={!isAdmin}>
                    <option value="">--</option>
                    <option value="Cadre">Cadre</option>
                    <option value="Agent de maitrise">Agent de maitrise</option>
                    <option value="Employe">Employe</option>
                    <option value="Ouvrier">Ouvrier</option>
                  </select>
                </div>
                <div className="fiche-field">
                  <label>Temps de travail</label>
                  <select value={ficheContratData.temps_travail || ''} onChange={e => setFicheContratData(d => ({ ...d, temps_travail: e.target.value }))} disabled={!isAdmin}>
                    <option value="">--</option>
                    <option value="Temps plein">Temps plein</option>
                    <option value="Temps partiel 80%">Temps partiel 80%</option>
                    <option value="Temps partiel 50%">Temps partiel 50%</option>
                    <option value="Mi-temps">Mi-temps</option>
                  </select>
                </div>
                <div className="fiche-field">
                  <label>Salaire brut contractuel</label>
                  <input type="number" step="0.01" value={ficheContratData.salaire_brut_contractuel || ''} onChange={e => setFicheContratData(d => ({ ...d, salaire_brut_contractuel: e.target.value }))} disabled={!isAdmin} placeholder="0.00" />
                </div>
                <div className="fiche-field">
                  <label>Variable contractuel</label>
                  <input type="number" step="0.01" value={ficheContratData.variable_contractuel || ''} onChange={e => setFicheContratData(d => ({ ...d, variable_contractuel: e.target.value }))} disabled={!isAdmin} placeholder="0.00" />
                </div>
              </div>

              <div className="fiche-contrat-elements">
                <h4>Elements complementaires</h4>
                {ficheContratElements.length > 0 && (
                  <table className="fiche-elements-table">
                    <thead><tr><th>Element</th><th>Montant</th>{isAdmin && <th></th>}</tr></thead>
                    <tbody>
                      {ficheContratElements.map(el => (
                        <tr key={el.id}>
                          <td>{el.libelle}</td>
                          <td>
                            <input type="number" step="0.01" value={el.montant} onChange={e => {
                              const val = e.target.value;
                              setFicheContratElements(prev => prev.map(x => x.id === el.id ? { ...x, montant: val } : x));
                            }} onBlur={e => handleUpdateContratElementMontant(el, e.target.value)} disabled={!isAdmin} />
                          </td>
                          {isAdmin && <td><button className="fiche-delete-btn" onClick={() => handleDeleteContratElement(el.id)} title="Supprimer">&times;</button></td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {isAdmin && (
                  <div className="fiche-add-element">
                    <select value={ficheNewElementTypeId} onChange={e => setFicheNewElementTypeId(e.target.value)}>
                      <option value="">Ajouter un element...</option>
                      {typesCharges.filter(tc => !ficheContratElements.some(el => el.type_charge_id === tc.id)).map(tc => (
                        <option key={tc.id} value={tc.id}>{tc.libelle}</option>
                      ))}
                    </select>
                    <input type="number" step="0.01" placeholder="Montant" value={ficheNewElementMontant} onChange={e => setFicheNewElementMontant(e.target.value)} />
                    <button onClick={handleAddContratElement} disabled={!ficheNewElementTypeId}>+</button>
                  </div>
                )}
              </div>
            </div>

            <div className="fiche-contrat-footer">
              {isAdmin && <button className="fiche-save-btn" onClick={saveFicheContrat} disabled={ficheSaving}>{ficheSaving ? 'Sauvegarde...' : 'Enregistrer'}</button>}
              <button className="fiche-cancel-btn" onClick={async () => { if (isAdmin) await saveFicheContrat(); setFicheContratSalarieId(null); }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale coefficient */}
      {showCoeffModal && (
        <div className="coeff-modal-overlay" onClick={() => setShowCoeffModal(false)}>
          <div className="coeff-modal" onClick={e => e.stopPropagation()}>
            <h3>Coefficient charges par defaut</h3>
            <p style={{ fontSize: '0.825rem', color: '#6b7280', margin: '8px 0 16px' }}>
              Utilise pour estimer le "Total verse" quand aucun mois reel n'existe pour le collaborateur.
              Ex: 1.45 = charges patronales de 45% du brut.
            </p>
            <input type="number" step="0.01" min="1" max="3" value={coeffInput} onChange={e => setCoeffInput(e.target.value)} />
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={handleSaveCoeff}>Enregistrer</button>
              <button className="btn-cancel" onClick={() => setShowCoeffModal(false)}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
